import React, { ChangeEvent, useEffect, useState, useRef, Fragment } from "react";
import { useHistory, useParams, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import axios from "axios";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faTrash,
  faPenToSquare,
  faArrowRight,
  faCircleExclamation,
  faMinus,
  faRotate,
  faSlidersSimple,
  faArrowUpRightFromSquare,
  faExclamationCircle,
  faQuestionCircle,
} from "@fortawesome/pro-regular-svg-icons";
import { faXmark, faChevronDown, faChevronUp, faArrowLeftArrowRight, faUpload } from "@fortawesome/pro-solid-svg-icons";

import { GetProductOptions, PutProduct, TPutProduct } from "api/rpc/2024-04/facilityAdmin/product/product";

import { IProduct, IVariant, TComboVariants } from "redux/reducers/models/product";
import { TAccountingReference, TModifierGroup, TTaxLines } from "redux/reducers/models/facility";
import {
  accountingReferenceMatchesQuery,
  formatAccountingReferenceLabel,
} from "utils/formatAccountingReferenceLabel";

import { useAppDispatch, useAppSelector } from "hooks/redux";
import useModal from "hooks/modals/useModal";
import {
  displayCurrency,
  handleChangeEventInput,
  isWholeNumber,
  roundNumber,
  unsavedChangesExist,
} from "helpers/Helpers";
import { LocaleCurrency } from "helpers/Locale";
import { showGiftCardRestrictFacilityToggle } from "helpers/giftCardRestrictFacility";
import { isNoTaxOption, taxLineSelectOptions, taxLineSelectValue } from "./taxLineSelect";

import Page from "components/page/Page";
import Card from "components/card/Card";
import PresetAndCustomColorPicker from "components/colorpicker/PresetAndCustomColorPicker";
import Popup from "components/popup/Popup";
import Sheet from "components/sheet/Sheet";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input/Input";
import { Select } from "components/select/";
import SelectNew from "components/select/SelectNew";
import { ButtonNew } from "components/buttonNew";
import TextEditor from "components/textEditor/textEditor";
import Checkbox from "components/form/checkbox/Checkbox";
import Toggle from "components/form/toggle/Toggle";
import Search from "components/search/Search";
import Spin from "components/spin/spin";
// import Stack from "components/stack/Stack";
import Tabs from "components/tabs/Tabs";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import TimePick from "components/timePick/TimePick";
import { Badge } from "components/badge/Badge";
import Portal from "elements/Portal";
import Callout from "components/callout/Callout";

import TagInputSelect from "components/tagInputSelect/TagInputSelect";
import Divider from "components/divider";

import "./Product.scss";
// import ModifierGroup from "../../settings/modifierGroup/ModifierGroup";

import CopyLink from "components/copyLink/CopyLink";
import { GetInventoryLogsAll } from "api/rpc/2024-04/facilityAdmin/product/inventory";
import { StatusCode } from "api/protocols";
import { showError, showSuccess } from "redux/actions/ui";
import InventoryLogs from "components/inventoryLog/inventoryLog";
import { GetDiscount, IPostDiscount, PostDiscount } from "api/rpc/2024-04/facilityAdmin/product/discount";
import ActionBar from "components/actionBar/ActionBar";
import useEditProduct from "./useEditProduct";
import GiftCardProductSettingsCard from "./GiftCardProductSettingsCard";
import { negativeCheckHelper } from "../settings/floorPlan/floorPlanEdit/FloorPlanEdit";
import ProductAvailabilityWarning from "../product/edit/ProductAvailabilityWarning";
import DataTable from "components/dataTable/DataTable";
import FacilityInventoryTable from "./FacilityInventoryTable";
import AttachProductAsComboModal from "../product/modals/AttachProductAsComboModal";
import IncludedMembershipsCard, {
  TPendingIncludedMembership,
} from "../product/modals/IncludedMembershipsCard";
import AddonMappingsCard, {
  TAddonModifierVariant,
  TPendingAddonMapping,
  modifierGroupsToAddonVariants,
} from "../product/modals/AddonMappingsCard";
import { GetGroupMembershipConfig } from "api/rpc/2024-04/facilityAdmin/product/groupMembership";
import {
  addonMappingsChanged,
  includedMembershipsChanged,
  persistAddonMappings,
  persistIncludedMemberships,
} from "helpers/groupMembershipConfig";
import UpdateProductAsComboModal from "../product/modals/UpdateProductAsComboModal";
import InventoryHistoryTable from "./InventoryHistoryTable";
import RegisterInventoryTransferTable from "./RegisterInventoryTransferTable";
import ReactDOM from "react-dom";
import { GetMembership, PutMembership, TFacilityMembership } from 'api/rpc/2024-04/facilityAdmin/membership/membership';

import { OverlayTrigger, Tooltip } from "react-bootstrap";

export default function ProductEdit() {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation<{ tab?: number }>();
  const { productId } = useParams<{ productId: string }>();
  const dispatch = useAppDispatch();

  const { authStore, facilityStore } = useAppSelector(store => store);
  const showGiftCardFacilityRestriction = showGiftCardRestrictFacilityToggle(
    facilityStore.facility,
    facilityStore.facilities,
  );

  const {
    /** Editable value */
    product,
    setProduct,

    /** Readable value */
    productPreserved,

    labelVariants,
    setLabelVariants,

    forms,
    setForms,

    comboProducts,
    //setComboProducts,
    productModifiers,

    availableAtDate,
    setAvailableAtDate,

    availableUntilDate,
    setAvailableUntilDate,

    productImages,
    modifierGroups,
    accountingReferences,
    salesChannels,
    departments,
    vendors,
    tickets,
    productTypes,
    taxlines,
    kitchenLocations,
    productUnits,
    warehouseProducts,

    // Validation
    attempted,
    validated,

    // ASYNC functions
    saveProduct,
    attachProductToFacility,
    detachProductFromfacility,
    archiveProduct,
    duplicateProduct,
    printLabels,
    uploadProductImage,
    deleteProductImage,
    assignProductImageVariant,
    addComboProduct,
    removeProductFromCombo,
    updateComboProduct,
    loadTickets,
    loadVariantForms,
    loadWarehouseProducts,
    addTicket,
    removeTicket,
    updateInventory,
    attachModifierGroupToProduct,
    detachModifierGroupFromProduct,
    updateSalesChannel,
    saveFormToVariant,
    removeFormFromVariant,
    archiveVariant,
  } = useEditProduct(productId);

  const addonModifierVariants: TAddonModifierVariant[] = modifierGroupsToAddonVariants(productModifiers);

  const tabs = [
    {
      id: "overview",
      content: "Overview",
    },
    {
      id: "pricing",
      content: "Pricing & Taxes",
    },
    {
      id: "logistics",
      content: "Logistics",
    },
    {
      id: "variants",
      content: "Variants & Modifiers",
    },
    {
      id: "sales-channels",
      content: "Sales Channels",
    },
    {
      id: "media",
      content: "Media",
    },
  ];

  // Use States -----------------

  // Tabs
  const [selected, setSelected] = useState(typeof location.state?.tab === "number" ? location.state.tab : 0);

  // Overview

  const [, setSelectedComboProductIds] = useState<number[]>([]);

  //const [inventoryLogs, setinventoryLogs] = useState([]);

  const [detailsCard, setDetailsCard] = useState({
    internal_description_visible: false,
  });

  // Pricing
  const [markup, setMarkup] = useState<number>(0);
  const [selectedTaxLine, setSelectedTaxLine] = useState<number[]>([]);
  const [selectedTaxLineBeforeChanges, setSelectedTaxLineBeforeChanges] = useState<number[]>([]);
  const [hidePriceAfterTax, setHidePriceAfterTax] = useState<boolean>(false);
  const [priceAfterTax, setPriceAfterTax] = useState<number>(0);

  // Discount Modal
  const [customerTypes] = useState<any[]>([]);
  const [selectedCustomerType, setSelectedCustomerType] = useState<number[]>([]);
  //const [, setSalesChannel] = useState<any[]>([]);
  const [, setDiscounts] = useState<any[]>([]);
  //const [, setSelectedDiscount] = useState<number[]>([]);

  //Logistics
  const [logisticsCard, setLogisticsCard] = useState({
    productTypeSearch: null as string, //sets when product is returned.
    accountRefSearch: null as string,
    vendorSearch: null as string,
    productUnitSearch: null as string,
  });
  const [showAllFacilityInventories, setShowAllFacilityInventories] = useState<boolean>(false);
  const [warehouseProductSearch, setWarehouseProductSearch] = useState<string>("");
  const [inventoryLogs, setInventoryLogs] = useState([]);
  const [selectedComboVariant, setSelectedComboVariant] = useState<number[]>([]);

  const [, setOptions] = useState<number[]>([]);
  const [adjustQuantity, setAdjustQuantity] = useState<boolean>(false);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(true);

  // Use Refs -----------------
  const fetchDiscounts = useRef(false);
  const defaultsSet = useRef(false);

  const productAccountReference = accountingReferences.find(ref => ref.id === product?.accounting_reference_id);

  const comboSelected = product?.type?.toLowerCase() === "combo";
  const comboAccountingReferenceIds = new Set(
    (product?.variants ?? [])
      .reduce<TComboVariants[]>((comboVariants, variant) => comboVariants.concat(variant.combination_variants ?? []), [])
      .map(comboVariant => comboVariant.accounting_reference_id),
  );
  const comboAccountingReferenceLabels = accountingReferences
    .filter(ref => comboAccountingReferenceIds.has(ref.id))
    .map(ref => formatAccountingReferenceLabel(ref.title, ref.reference_number));

  // Use Memos -----------------
  //const comboSelected = product?.type === "Combo";
  //const regularComboSelected = comboSelected && !product.dynamic_combo;

  // const allComboProductsSelected = useMemo(() => {
  //   if (comboProducts == null || comboProducts.length === 0) {
  //     return false;
  //   } else {
  //     return comboProducts.every((comboProduct) => selectedComboProductIds.includes(comboProduct.id));
  //   }
  // }, [comboProducts, selectedComboProductIds]);

  // Use Modals -----------------
  const {
    state: kitchenLocationModal,
    updateModal: updateKitchenModal,
    closeModal: closeKitchenModal,
  } = useModal({
    kitchen_location_id: undefined,
    kitchen_location_id_2: undefined,
    kitchen_location_id_3: undefined,
  });

  const {
    state: printLabelModal,
    updateModal: updatePrintLabelModal,
    closeModal: closePrintLabelModal,
  } = useModal<{ offset: string; template_id: string }>({ offset: "", template_id: "5160" });

  const { state: archiveModal, updateModal: updateArchiveModal, closeModal: closeArchiveModal } = useModal();

  const {
    state: removeProductModal,
    updateModal: updateRemoveProductModal,
    closeModal: closeRemoveProductModal,
  } = useModal();

  const {
    state: duplicateProductModal,
    updateModal: updateDuplicateProductModal,
    closeModal: closeDuplicateProductModal,
  } = useModal();

  const {
    state: deleteVariantModal,
    updateModal: updateDeleteVariantModal,
    closeModal: closeDeleteVariantModal,
  } = useModal({ variantId: null });

  const {
    state: newComboProductModal,
    updateModal: updateNewComboProductModal,
    closeModal: closeNewComboProductModal,
  } = useModal();

  const {
    state: editComboProductModal,
    updateModal: updateEditComboProductModal,
    closeModal: closeEditComboProductModal,
  } = useModal({ selectedComboVariant: null as TComboVariants });

  const {
    state: addTicketModal,
    updateModal: updateAddTicketModal,
    closeModal: closeAddTicketModal,
  } = useModal({ search: "" });

  const { state: inventoryModal, updateModal: updateInventoryModal } = useModal({});

  const {
    state: taxModal,
    //updateModal: updateTaxModal,
    closeModal: closeTaxModal,
  } = useModal({ taxLineDropdowns: [] as { variant_id: number; tax_line_id: number }[] });

  const {
    state: modifierModal,
    updateModal: updateModifierModal,
    closeModal: closeModifierModal,
  } = useModal({ search: "", required: false, groupIds: [] as Array<number> });

  const {
    state: formsModal,
    updateModal: updateFormsModal,
    closeModal: closeFormsModal,
  } = useModal({
    formToVariantVisible: false,
    variant_id: -1,
    form_id: -1,
    removeFromVariantId: null,
    removeFormId: null,
    removeFormVisible: false,
  });

  const {
    state: discountModal,
    updateModal: updateDiscountModal,
    closeModal: closeDiscountModal,
  } = useModal({
    title: "",
    application: "order", // Temporary value until I know what this is
    scheduledDiscount: false,
    value_type: "percent",
    value: 0,
    customerType: [],
    has_customer: false,
    start_date: new Date(),
    end_date: new Date(),
    dateRange: [],
    startTime: null,
    endTime: null,
    salesChannel: 0,
    custom: false,
    tracked: false,
  });


  const [membershipState, setMembershipState] = useState<{
    memberships: TFacilityMembership[];
    loading: boolean;
  }>({
    memberships: [],
    loading: false,
  });

  const {
    state: attachMembershipModal,
    updateModal: updateAttachMembershipModal,
    closeModal: closeAttachMembershipModal,
  } = useModal({
    isOpen: false,
  });

  const [attachedMemberships, setAttachedMemberships] = useState<TFacilityMembership[]>([]);
  const [membershipSearchQuery, setMembershipSearchQuery] = useState("");
  const [selectedMembershipId, setSelectedMembershipId] = useState<number | null>(null);
  const [availableMemberships, setAvailableMemberships] = useState<TFacilityMembership[]>(undefined);
  const [includedMemberships, setIncludedMemberships] = useState<TPendingIncludedMembership[]>([]);
  const [includedMembershipsPreserved, setIncludedMembershipsPreserved] = useState<TPendingIncludedMembership[]>([]);
  const [addonMappings, setAddonMappings] = useState<TPendingAddonMapping[]>([]);
  const [addonMappingsPreserved, setAddonMappingsPreserved] = useState<TPendingAddonMapping[]>([]);

  // Use Effects -----------------

  // Overview
  // Keep Visual with an internal description
  useEffect(() => {
    // Toggle open if starting with an internal description
    if (!detailsCard.internal_description_visible && productPreserved?.internal_description) {
      setDetailsCard(prev => ({ ...prev, internal_description_visible: true }));
    }
  }, [detailsCard.internal_description_visible, productPreserved?.internal_description]);

  // Pricing
  // useEffect(() => {
  //   const price = Number(product?.price ?? 0);
  //   const cost = Number(product?.cost ?? 0);
  //   setMarkup(price / cost);
  // }, [product]);

  // useEffect(() => {
  //   if (!product?.cost) {
  //     showError("Cost is required");
  //   } else {
  //     setProduct(prev => ({
  //       ...prev,
  //       price: Number(roundNumber(product.cost * markup, 2)),
  //     }));
  //   }
  // }, [markup]);

  useEffect(() => {
    if (selected === 1 && !fetchDiscounts.current) {
      void loadDiscounts();
      fetchDiscounts.current = true;
    }
  }, [selected]);


  useEffect(() => {
    if (!defaultsSet.current && product) {
      void getProductOptions();
      setMarkup(((product?.price - product?.cost) / product?.cost) * 100);
      setPriceAfterTax(Number(getPriceAfterTax(product?.price ?? 0)));
      defaultsSet.current = true;
    }
  }, [product]);

  // Logistics
  // Keep Select search consistent with product type
  useEffect(() => {
    if (product?.type) {
      setLogisticsCard(prev => ({ ...prev, productTypeSearch: product.type }));
      setSelectedComboProductIds([]);
    }
  }, [product?.type]);

  useEffect(() => {
    if (product?.type === "Group Membership" && productId) {
      void loadGroupMembershipConfig();
    }
  }, [productId, product?.type]);

  useEffect(() => {
    if (accountingReferences && productAccountReference !== undefined) {
      setLogisticsCard(prev => ({
        ...prev,
        accountRefSearch: formatAccountingReferenceLabel(
          productAccountReference.title ?? "",
          productAccountReference.reference_number,
        ),
      }));
    }
  }, [accountingReferences, productAccountReference]);

  useEffect(() => {
    if (vendors && product?.vendor_id) {
      const vendor = vendors.find(value => value.id === product.vendor_id);

      setLogisticsCard(prev => ({ ...prev, vendorSearch: vendor ? vendor?.title : prev.vendorSearch }));
    }
  }, [vendors, product?.vendor_id]);

  useEffect(() => {
    if (productUnits && product?.product_unit_id) {
      const unit = productUnits.find(unit => unit.id === product.product_unit_id);

      setLogisticsCard(prev => ({ ...prev, productUnitSearch: unit ? unit?.name : prev.productUnitSearch }));
    }
  }, [productUnits, product?.product_unit_id]);

  // Lazy load tickets on first modal open
  useEffect(() => {
    const source = axios.CancelToken.source();

    if (addTicketModal.isOpen && tickets === undefined) {
      void loadTickets(null, source.token);
    }

    return () => source.cancel();
  }, [addTicketModal.isOpen]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadWarehouseProducts(warehouseProductSearch, source.token);
    return () => source.cancel();
  }, [warehouseProductSearch]);

  //Variants & Modifiers
  // Lazy load variant forms on first modal open
  useEffect(() => {
    const source = axios.CancelToken.source();

    if (formsModal.isOpen && forms.variants === undefined) {
      void loadVariantForms({ variant_ids: product.variants.map(val => val.id) }, source.token);
    }
  }, [formsModal.isOpen]);

  // Debouced 0.75ms because of <Search />
  useEffect(() => {
    const source = axios.CancelToken.source();
    if (product) {
      void loadTickets(addTicketModal.search, source.token);
    }
    return () => source.cancel();
  }, [addTicketModal.search]);

  useEffect(() => {
    if ((product?.variants?.length ?? 0) <= 1) {
      const taxLineIds =
        product?.variants?.[0]?.inventory_item?.inventory_level?.tax_lines?.map(line => line.id) ?? [];
      setSelectedTaxLine(taxLineIds);
      setSelectedTaxLineBeforeChanges(taxLineIds);
    }
  }, [product?.variants]);


  useEffect(() => {
    if (product?.id) {
      void loadProductMemberships();
    }
  }, [product?.id]);


  useEffect(() => {
    if (attachMembershipModal.isOpen) {
      void loadAvailableMemberships();
    }
  }, [attachMembershipModal.isOpen, membershipSearchQuery]);



  // API Calls -----------------
  // async function loadCustomerTypes() {
  //   const res = await GetCustomerType(
  //     {
  //       booking_category: true,
  //     },
  //     false,
  //   );
  //   if (res.status !== StatusCode.OK) {
  //     return;
  //   }
  //   setCustomerTypes(res.data);
  // }

  // async function loadSalesChannels() {
  //   const params = { id: productId };
  //   const res = await GetSalesChannels(params, false);

  //   if (res.status !== StatusCode.OK) {
  //     return;
  //   }

  //   setSalesChannel(Array.isArray(res.data) ? res.data : [res.data]);
  // }


  async function loadDiscounts() {
    const res = await GetDiscount();

    if (res.status !== StatusCode.OK) {
      return;
    }

    setDiscounts(Array.isArray(res.data) ? res.data : [res.data]);
  }

  async function addDiscount(discount: any) {
    if (discount.scheduledDiscount) {
      if (discount.start_date === null || discountModal.end_date === null) {
        dispatch(showError("Start and end date are required"));
        return;
      }
      if (discount.startTime > discount.endTime) {
        dispatch(showError("Start time must be before end time"));
        return;
      }
    }

    selectedCustomerType.forEach((customerType: any) => {
      discount.customerType.push(customerType.id);
    });

    // if (discount.title === "" ||
    //   discount.value_type === null ||
    //   discount.value === 0 ||
    //   discount.customerType.length === 0 ||
    //   discount.salesChannel === 0
    // ) {
    //   dispatch(showError("All fields are required"));
    //   console.log("Title", discount.title);
    //   console.log("Value Type", discount.value_type);
    //   console.log("Value", discount.value);
    //   console.log("Customer Type", discount.customerType);
    //   console.log("Sales Channel", discount.salesChannel);
    //   return;
    // }

    const iDiscount: IPostDiscount = {
      title: discount.title,
      application: discount.application,
      value_type: "percent",
      value: discount.value,
      custom: discount.custom,
      tracked: false,
    };

    const res = await PostDiscount(iDiscount, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error adding discount")));
      return;
    }

    dispatch(showSuccess(t("Discount added successfully")));

    void loadDiscounts();
  }

  async function getProductOptions() {
    const res = await GetProductOptions({ product_id: productId }, false);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading product options")));
      return;
    }

    setOptions(res.data as number[]);
  }

  // Handlers -----------------

  const handleTabChange = (selectedTabIndex: number) => {
    setSelected(selectedTabIndex);
  };

  // const canRemoveComboProduct = authStore.user?.permissions?.products_edit;
  // const canUpdateComboProduct = authStore.user?.permissions?.products_edit && !product?.dynamic_combo;

  function updateProduct() {
    const { ...rest } = product;
    let onDateTime = null;
    let untilDateTime = null;

    if (availableAtDate !== null && product.available_on_time !== null) {
      const tempOnDateTime = availableAtDate.toLocaleDateString() + " " + product.available_on_time;
      onDateTime = new Date(tempOnDateTime).toISOString();
    }

    if (availableUntilDate !== null && product.available_on_time !== null) {
      const tempUntilDateTime = availableUntilDate.toLocaleDateString() + " " + product.unavailable_on_time;
      untilDateTime = new Date(tempUntilDateTime).toISOString();
    }

    const warehouseProductsParam = product?.warehouse_products?.reduce((currProducts, product) => {
      if (currProducts?.findIndex(searchedProduct => searchedProduct?.id === product?.id) === -1) {
        return [...currProducts, product];
      } else {
        return currProducts;
      }
    }, [] as Array<IProduct & { variant_id?: number; inventory_units?: string }>);

    const putProduct: TPutProduct & { cannot_be_sold: boolean } = {
      ...rest,
      //The first variant's taxable value determines if the checkbox is set. Backend handles setting all variant's taxable value
      taxable: product?.variants[0]?.taxable,
      meta: {
        ...rest.meta,
        kitchen_location_id: negativeCheckHelper(product.meta.kitchen_location_id),
        kitchen_location_id_2: negativeCheckHelper(product.meta.kitchen_location_id_2),
        kitchen_location_id_3: negativeCheckHelper(product.meta.kitchen_location_id_3),
        loyalty_points_override:
          product?.variants?.length === 1 ? product?.variants[0]?.meta?.loyalty_points_override : undefined,
      },
      available_on: onDateTime,
      unavailable_on: untilDateTime,
      department_id: negativeCheckHelper(product.department_id),
      category_id: negativeCheckHelper(product.category_id),
      subcategory_id: negativeCheckHelper(product.subcategory_id),
      warehouse_product_ids: product?.warehouse
        ? []
        : warehouseProductsParam?.map(product => ({
          warehouse_product_id: product?.id,
          inventory_units: product?.inventory_units ?? null,
        })),
    };

    void saveProduct(putProduct);

    if (product.variants?.length === 1) {
      void updateInventory({
        variant_id: product?.variants[0].id,
        tax_line_ids: selectedTaxLine,
        refreshProduct: true,
      });
    }

    void saveGroupMembershipConfig();
  }

  function syncKitchenLocations() {
    setProduct(prev => ({
      ...prev,
      meta: {
        ...prev.meta,
        kitchen_location_id: kitchenLocationModal.kitchen_location_id,
        kitchen_location_id_2: kitchenLocationModal.kitchen_location_id_2,
        kitchen_location_id_3: kitchenLocationModal.kitchen_location_id_3,
      },
    }));
    closeKitchenModal();
  }

  // Only called if !isPrintLabelsDisabled
  function printLabelModalOk() {
    void printLabels(printLabelModal.offset, printLabelModal.template_id);

    closePrintLabelModal();
    setLabelVariants(
      labelVariants.map(val => ({
        ...val,
        quantity: 0,
      })),
    );
  }
  function isPrintLabelsDisabled() {
    return (
      (printLabelModal.offset !== "" && !isWholeNumber(printLabelModal.offset)) ||
      labelVariants.filter(variant => variant.quantity !== 0).length === 0 ||
      labelVariants.some(variant => variant.quantity !== 0 && !isWholeNumber(String(variant.quantity)))
    );
  }

  // Only called with modifierModal.groupIds.length > 0
  function handleModifierModalOnOk() {
    const modifier_groups = [
      {
        modifier_group_id: modifierModal.groupIds[0],
        required: modifierModal.required,
      },
    ];

    if (product?.variant_multiselect) {
      setProduct(prev => ({ ...prev, variant_multiselect: false }));
      void PutProduct({ id: product.id, variant_multiselect: false }, false);
    }

    void attachModifierGroupToProduct(modifier_groups, true);

    closeModifierModal();
  }

  // function moveToProductOnlineStore() {
  //   history.push("/online-store/" + facilityStore?.facility?.short_name + "/product/" + product.handle);
  // }

  // Overview

  // Pricing
  const toggleTaxLines = (value: number | string) => {
    if (isNoTaxOption(value)) {
      setSelectedTaxLine([]);
      return;
    }

    const taxLineId = Number(value);
    setSelectedTaxLine(prev => {
      const ids = prev ?? [];
      return ids.includes(taxLineId) ? ids.filter(v => v !== taxLineId) : [...ids, taxLineId];
    });
  };

  useEffect(() => {
    if (product && taxlines) {
      setPriceAfterTax(getPriceAfterTax(product.price));
    }
  }, [selectedTaxLine, taxlines]);

  // const toggleDiscounts = (value: number) => {
  //   setSelectedDiscount((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
  // };

  const toggleCustomerType = (value: number) => {
    setSelectedCustomerType(prev => (prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]));
  };

  function getTaxNames() {
    const taxNames = [];
    for (let i = 0; i < selectedTaxLine.length; i++) {
      taxNames[i] = taxlines.find(tax => tax.id === selectedTaxLine[i])?.name ?? "";
    }
    return taxNames.join(", ");
  }

  function setDateRangeStart(date: Date) {
    updateDiscountModal({ start_date: date });
  }

  function setDateRangeEnd(date: Date) {
    updateDiscountModal({ end_date: date });
  }

  function handleCustomerTypeCheckbox(event: ChangeEvent<HTMLInputElement>, index: number) {
    const { checked } = event.target;
    const newDiscounts = [...discountModal.customerType];
    newDiscounts[index] = { ...newDiscounts[index], checked: checked };
    updateDiscountModal({ customerType: newDiscounts });
  }

  function handleAllCustomerTypeCheckbox(event: ChangeEvent<HTMLInputElement>) {
    const { checked } = event.target;
    const newDiscounts = [...discountModal.customerType];
    for (let i = 0; i < newDiscounts.length; i++) {
      newDiscounts[i] = { ...newDiscounts[i], has_customer: checked };
    }
    updateDiscountModal({ customerType: newDiscounts });
  }

  function handleRemoveTaxLine(variant: IVariant, taxLineId: number) {
    let currentTaxIds = variant.inventory_item.inventory_level.tax_lines.map(line => line.id);
    currentTaxIds = currentTaxIds.filter(id => id !== taxLineId);

    void updateInventory({ variant_id: variant.id, tax_line_ids: currentTaxIds, refreshProduct: true });
  }

  function handleTaxDropdown(tax_line_id: number, variant: IVariant) {
    // const currentLists = [...taxModal.taxLineDropdowns];

    const allIds = variant.inventory_item.inventory_level.tax_lines.map(line => line.id);
    // const foundIndex = currentLists.findIndex(list => variant.id === list.variant_id);

    // if (foundIndex === -1) {
    allIds.push(tax_line_id);
    //   // updateTaxModal({ taxLineDropdowns: [...currentLists, { variant_id: variant.id, tax_line_id: tax_line_id }] });
    // } else {
    //   currentLists.map(list => {
    //     if (list.variant_id == variant.id) {
    //       allIds.push(tax_line_id);
    //       return { ...list, tax_line_id: tax_line_id };
    //     }
    //     return list;
    //   });
    //   updateTaxModal({ taxLineDropdowns: currentLists });
    // }

    void updateInventory({ variant_id: variant.id, tax_line_ids: allIds, refreshProduct: true });
  }

  // Logistics
  /** Update product.meta.cannot_be_sold value */
  function toggleCannotBeSold() {
    const params = {
      ...product,
      meta: {
        ...product.meta,
        cannot_be_sold: !product.meta?.cannot_be_sold,
        kitchen_location_id: negativeCheckHelper(product.meta?.kitchen_location_id),
        kitchen_location_id_2: negativeCheckHelper(product.meta?.kitchen_location_id_2),
        kitchen_location_id_3: negativeCheckHelper(product.meta?.kitchen_location_id_3),
      },
      department_id: negativeCheckHelper(product.department_id),
      category_id: negativeCheckHelper(product.category_id),
      subcategory_id: negativeCheckHelper(product.subcategory_id),
    };

    void saveProduct(params);
  }

  function logisticsCardSearch(
    key: keyof { productTypeSearch: string; accountRefSearch: string; vendorSearch: string; productUnitSearch: string },
    query: string,
  ) {
    // handle the search query
    setLogisticsCard(prev => ({ ...prev, [key]: query }));

    // If search is cleared, remove the product param
    if (logisticsCard[key] !== null && query?.length === 0) {
      switch (key) {
        case "accountRefSearch": {
          setProduct(prev => ({ ...prev, accounting_reference_id: undefined }));
          break;
        }
        case "productTypeSearch": {
          setProduct(prev => ({ ...prev, type: undefined }));
          break;
        }
        case "vendorSearch": {
          setProduct(prev => ({ ...prev, vendor_id: undefined }));
          break;
        }
        case "productUnitSearch": {
          setProduct(prev => ({ ...prev, product_unit_id: undefined }));
          break;
        }
        default:
          return;
      }
    }
  }

  function handleComboProductModalOk(newId: number) {
    const params = {
      base_variant_id: product.variants[0].id,
      variant_id: newId,
    };

    void addComboProduct(params);
    // console.log(newProduct);

    // setProduct((prevState) => ({ ...prevState, price: newProduct.price }));
    // setComboProducts(newProduct.variants.length === 0 ? [] : newProduct.variants[0].combination_variants);

    closeNewComboProductModal();
  }

  /** TODO: Connect to backend || updateComboProduct function within useEditProduct hook */
  function handleUpdateComboProductModalOk(updateId: number, cost: string, price: string, quantity: string) {
    const params = {
      base_variant_id: product.variants[0].id,
      variant_id: updateId,
      cost: cost,
      price: price,
      quantity: quantity,
    };

    void updateComboProduct(params);
    // console.log(newProduct);

    // setProduct((prevState) => ({ ...prevState, price: newProduct.price }));
    // setComboProducts(newProduct.variants.length === 0 ? [] : newProduct.variants[0].combination_variants);

    closeEditComboProductModal();
  }

  function productTicketQuantityOnChange(ticketId: number, value: string) {
    setProduct(prev => ({
      ...prev,
      variants: prev.variants.map(variant => ({
        ...variant,
        tickets: variant.tickets.map(ticket => {
          if (ticket.id === ticketId) {
            return {
              ...ticket,
              quantity: Number(value),
            };
          } else {
            return ticket;
          }
        }),
      })),
    }));
  }

  function handleAddTicket(ticketId: number) {
    void addTicket(ticketId, null, null, true);
    closeAddTicketModal();
  }

  function handleUpdateInventory(quantity: number, price: number, cost: number) {
    void updateInventory({ variant_id: null, quantity, price, cost, refreshProduct: true });
    updateInventoryModal({ isOpen: false });
  }

  function generateSKU(): string {
    let sku = "";
    if (product?.title) {
      const titleWords = product.title.split(" ");
      const name = titleWords.map(word => word.charAt(0).toUpperCase()).join("");
      sku = name + "-";
    }
    return sku + Math.floor(Math.random() * 10000).toString();
  }

  // Permissions

  // Variants & Modifiers
  function variantFacilityAccessChange(id: number, checked: boolean) {
    setProduct(prev => ({
      ...prev,
      variants: prev.variants.map(variant => {
        if (variant.id == id) {
          return { ...variant, facility_access: checked };
        }
        return { ...variant };
      }),
    }));
  }
  // Sales Channels
  /** add / removes the sales channel immediately based on the targets checked value. */
  function handleSalesChannelUpdate(e: ChangeEvent<HTMLInputElement>) {
    const { id, checked } = e.target;

    const params = {
      sales_channel_id: Number(id),
      remove: !checked,
    };

    void updateSalesChannel(params);
  }

  function handleFormsModalOk() {
    const params = {
      variant_id: formsModal.variant_id,
      form_id: formsModal.form_id,
    };
    void saveFormToVariant(params);

    setForms(prev => ({ ...prev, variants: undefined }));
    updateFormsModal({ formToVariantVisible: false, variant_id: null, form_id: null });
  }

  function handleAvailableOnToggle(e: ChangeEvent<HTMLInputElement>) {
    const { checked } = e.target;

    if (!checked) {
      setAvailableAtDate(null);
      setProduct(prev => ({ ...prev, available_on: null, available_on_time: null }));
    } else {
      // re-apply previous date when toggled on
      if (productPreserved.available_on) {
        setProduct(prev => ({
          ...prev,
          available_on: productPreserved.available_on,
          available_on_time: productPreserved.available_on_time,
        }));
        setAvailableAtDate(new Date(productPreserved.available_on));
      } else {
        const newDate = new Date();
        newDate.setHours(0, 0, 0, 0);

        setProduct(prev => ({
          ...prev,
          available_on: newDate.toLocaleDateString(),
          available_on_time: "12:00",
        }));

        setAvailableAtDate(newDate);
      }
    }
  }

  function handleAvailableUntilToggle(e: ChangeEvent<HTMLInputElement>) {
    const { checked } = e.target;

    if (!checked) {
      setAvailableUntilDate(null);
      setProduct(prev => ({ ...prev, unavailable_on: null, unavailable_on_time: null }));
    } else {
      // try to re-apply previous date when toggled on
      if (productPreserved.unavailable_on) {
        setProduct(prev => ({
          ...prev,
          unavailable_on: productPreserved.unavailable_on,
          unavailable_on_time: productPreserved.unavailable_on_time,
        }));
        setAvailableUntilDate(new Date(productPreserved.unavailable_on));
      } else {
        const newDate = new Date();
        newDate.setHours(0, 0, 0, 0);
        newDate.setDate(newDate.getDate() + 7); // 1 Week after todays date

        setProduct(prev => ({
          ...prev,
          unavailable_on: newDate.toLocaleDateString(),
          unavailable_on_time: "12:00",
        }));

        setAvailableUntilDate(newDate);
      }
    }
  }

  function handleAvailableOnDateSelectorChange(selectedDate: Date) {
    const tempDate = new Date(selectedDate).toLocaleDateString();

    // Handle range error with available_at and available_until
    if (availableUntilDate) {
      const tempStart = new Date(selectedDate.toLocaleDateString() + " " + product.available_on_time);
      const tempEnd = new Date(availableUntilDate.toLocaleDateString() + " " + product.unavailable_on_time);

      if (tempStart > tempEnd) {
        const monthAfter = new Date(selectedDate.getTime());
        monthAfter.setMonth(monthAfter.getMonth() + 1);

        setProduct(prev => ({ ...prev, unavailable_on: monthAfter.toLocaleDateString() }));
        setAvailableUntilDate(monthAfter);
      }
    }

    setProduct(prev => ({ ...prev, available_on: tempDate }));
    setAvailableAtDate(selectedDate);
  }

  function handleAvailableUntilDateSelectorChange(selectedDate: Date) {
    const tempDate = new Date(selectedDate).toLocaleDateString();

    setAvailableUntilDate(selectedDate);
    setProduct(prev => ({ ...prev, available_on: tempDate }));
  }

  function handleTimeClear(id: "available_on_time" | "unavailable_on_time") {
    if (id === "available_on_time") {
      setProduct(prevState => ({
        ...prevState,
        available_on: null,
        available_on_time: null,
      }));

      return;
    }
    if (id === "unavailable_on_time") {
      setProduct(prevState => ({
        ...prevState,
        unavailable_on: null,
        unavailable_on_time: null,
      }));
      return;
    }
  }

  function handleTimeAvailability(id: "available_on_time" | "unavailable_on_time", value: string) {
    if (value === undefined) {
      setProduct(prev => ({ ...prev, [id]: null }));
      return;
    }

    if (value === null || value?.length === 0) {
      handleTimeClear(id);
      return;
    }

    if ((product.available_on as string) === (product.unavailable_on as string)) {
      const dateVal = id === "available_on_time" ? "available_on" : "unavailable_on";
      const dateValue = new Date(`${product[dateVal] as string} ${value}`);

      if (id === "available_on_time") {
        const unavailableDate = new Date((product.unavailable_on as string) + " " + product.unavailable_on_time);
        if (dateValue < unavailableDate) {
          setProduct(prev => ({ ...prev, available_on_time: value }));
        }
      }
      if (id === "unavailable_on_time") {
        const availableDate = new Date((product.available_on as string) + " " + product.available_on_time);
        if (dateValue > availableDate) {
          setProduct(prev => ({ ...prev, unavailable_on_time: value }));
        }
      }

      return;
    }

    setProduct(prev => ({ ...prev, [id]: value }));
  }

  /*
    - available_on date could previously be set to a date before today and be a valid date.
    - available_on date can not be updated to date value before the current date.
    - <DatePick /> component `status` param visually indicates this edge case when applicable
  */
  function availableOnDateIsValid() {
    if (availableAtDate === null) {
      return true;
    }

    const wantedDate = new Date(`${availableAtDate.toLocaleDateString()} ${product?.available_on_time}`);
    const preservedDate = new Date(
      `${productPreserved?.available_on as string} ${productPreserved?.available_on_time}`,
    );

    if (
      product.available_on_time === null ||
      (wantedDate < new Date() && wantedDate.getTime() !== preservedDate.getTime())
    ) {
      return false;
    } else {
      return true;
    }
  }

  function availableUntilDateIsValid() {
    if (availableUntilDate === null) {
      return true;
    }

    const wantedDate = new Date(`${availableUntilDate?.toLocaleDateString()} ${product?.unavailable_on_time}`);
    const startDate = new Date(`${availableAtDate?.toLocaleDateString()} ${product?.available_on_time}`);

    if (product.unavailable_on_time === null || wantedDate < startDate) {
      return false;
    }

    return true;
  }

  const readOnly =
    (authStore.user?.permissions && !authStore.user?.permissions?.products_edit) ||
    (facilityStore.facility?.permissions && !facilityStore.facility?.permissions?.products_edit) ||
    !product;

  function handleRemoveFormsModalOk() {
    const params = {
      form_id: formsModal.removeFormId,
      variant_id: formsModal.removeFromVariantId,
    };

    void removeFormFromVariant(params);

    setForms(prev => ({ ...prev, variants: undefined }));
    updateFormsModal({ removeFormVisible: false, removeFormId: null, removeFromVariantId: null });
  }

  // function handleRemoveComboProduct(combo: TComboVariants) {
  //   if (canRemoveComboProduct) {
  //     void removeProductFromCombo([combo]);
  //     setSelectedComboProductIds((prev) => prev.filter((selectedId) => selectedId !== combo.id));
  //   }
  // }

  // function handleUpdateComboProduct(combo: TComboVariants) {
  //   if (canUpdateComboProduct) {
  //     updateEditComboProductModal({ isOpen: true, selectedComboVariant: combo });
  //   }
  // }

  // function moveToProductOnlineStore() {
  //   history.push("/online-store/" + facilityStore?.facility?.short_name + "/product/" + product.handle);
  // }
  async function displayInventoryLog() {
    updateInventoryModal({
      isOpen: true,
    });
    const response = await GetInventoryLogsAll(
      { inventory_level_id: product?.variants[0]?.inventory_item?.inventory_level?.id },
      false,
    );

    if (response.status !== StatusCode.OK) {
      dispatch(showError("Error displaying inventory logs"));
      return;
    }

    setInventoryLogs(response.data as any[]);
  }

  function handleMarkupChange(value: number) {
    if (!product?.cost) {
      showError("Cost is required");
    } else {
      setMarkup(value);
      setProduct(prev => ({
        ...prev,
        price: Number(roundNumber(product.cost * (value / 100) + product.cost, 2)),
      }));
      setPriceAfterTax(Number(getPriceAfterTax(Number(roundNumber(product.cost * (value / 100) + product.cost, 2)))));
    }
  }

  function handleCostChange(value: number) {
    if (value === 0) {
      setMarkup(0);
      setProduct(prev => ({
        ...prev,
        cost: Number(roundNumber(value, 2)),
      }));
      return;
    }
    const price = Number(product?.price ?? 0);
    setMarkup(((price - value) / value) * 100);
    setProduct(prev => ({
      ...prev,
      cost: Number(roundNumber(value, 2)),
    }));
  }

  function handlePriceChange(value: number) {
    const cost = Number(product?.cost ?? 0);
    setMarkup(((value - cost) / cost) * 100);
    setProduct(prev => ({
      ...prev,
      price: Number(roundNumber(value, 2)),
    }));
    setPriceAfterTax(Number(getPriceAfterTax(value)));
  }

  function handlePriceAfterTaxChange(value: number) {
    const cost = Number(product?.cost ?? 0);

    let totalTaxRate = 0;
    for (let i = 0; i < selectedTaxLine.length; i++) {
      const taxLine = taxlines.find(tax => tax.id === selectedTaxLine[i]);
      if (taxLine) {
        totalTaxRate += Number(taxLine.tax);
      }
    }

    const price = value / (1 + totalTaxRate);

    setMarkup(((price - cost) / cost) * 100);
    setProduct(prev => ({
      ...prev,
      price: Number(roundNumber(price, 2)),
    }));
    setPriceAfterTax(value);
  }

  function getPriceAfterTax(value: number) {
    const originalPrice = value;
    let price = value;

    if (product.type?.toLowerCase() === "combo" && product.dynamic_combo !== true) {
      price = 0;
      const combos = product.variants?.[0]?.combination_variants ?? [];
      for (const combo of combos) {
        const comboTaxRate = combo?.inventory_item.inventory_level.tax_lines?.reduce((sum, tax_line) => sum + tax_line.tax, 0) ?? 0;
        const comboPrice = combo.combo_pivot.quantity * combo.combo_pivot.price * (1 + comboTaxRate);
        price += comboPrice;
      }
    } else {
      const taxLineIds = selectedTaxLine ?? [];
      for (let i = 0; i < taxLineIds.length; i++) {
        price += originalPrice * taxlines.find(tax => tax.id === taxLineIds[i])?.tax;
      }
    }

    return Number(roundNumber(price, 2));
  }

  async function handleArchiveVariant() {
    if (!deleteVariantModal.variantId) {
      return;
    }
    await archiveVariant({ id: deleteVariantModal.variantId });
    closeDeleteVariantModal();
  }

  // function handleSelectAllComboProducts() {
  //   if (comboProducts == null || comboProducts.length === 0) {
  //     return;
  //   } else if (allComboProductsSelected) {
  //     setSelectedComboProductIds([]);
  //   } else {
  //     setSelectedComboProductIds(comboProducts.map((comboProduct) => comboProduct.id));
  //   }
  // }

  // function handleSelectComboProduct(comboId: number) {
  //   if (selectedComboProductIds.includes(comboId)) {
  //     setSelectedComboProductIds((prev) => prev.filter((selectedComboId) => selectedComboId !== comboId));
  //   } else {
  //     setSelectedComboProductIds((prev) => [...prev, comboId]);
  //   }
  // }

  // function handleComboProductsRemoval() {
  //   if (canRemoveComboProduct) {
  //     void removeProductFromCombo(comboProducts.filter((combo) => selectedComboProductIds.includes(combo.id)));
  //     setSelectedComboProductIds([]);
  //   }
  // }
  //

  function onInventoryUnitsChange(e: React.ChangeEvent<HTMLInputElement>, index: number) {
    const { value } = e.target;
    const updatedWarehouseProducts = [...product.warehouse_products];
    if (updatedWarehouseProducts[index]) {
      updatedWarehouseProducts[index] = { ...updatedWarehouseProducts[index], inventory_units: value };
      setProduct(prev => ({ ...prev, warehouse_products: updatedWarehouseProducts }));
    }
  }

  // Load Memberships on Products

  //console.log(membershipState);
  async function loadProductMemberships() {
    if (!product?.id) {
      return;
    }

    setMembershipState(prev => ({ ...prev, loading: true }));

    const res = await GetMembership({ limit: 500, offset: 0, archived: false }, false);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading memberships")));
      setMembershipState(prev => ({ ...prev, loading: false }));
      return;
    }

    const allMemberships = Array.isArray(res.data) ? res.data : [];
    const membershipsUsingProduct = allMemberships.filter(m => m.product_id === Number(product.id));

    setAttachedMemberships(membershipsUsingProduct);
    setMembershipState({
      memberships: membershipsUsingProduct,
      loading: false,
    });
  }

  async function loadGroupMembershipConfig() {
    const res = await GetGroupMembershipConfig({ product_id: Number(productId) }, false);
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading group membership configuration")));
      return;
    }

    const included = (res.data?.included ?? []).map(row => ({
      id: row.id,
      membership_id: row.membership_id,
      quantity: row.quantity,
      membership: row.membership,
    }));
    const mappings = (res.data?.addon_mappings ?? []).map(row => ({
      id: row.id,
      modifier_variant_id: row.modifier_variant_id,
      membership_id: row.membership_id,
      membership: row.membership,
      modifier_variant: row.modifier_variant,
    }));

    setIncludedMemberships(included);
    setIncludedMembershipsPreserved(included);
    setAddonMappings(mappings);
    setAddonMappingsPreserved(mappings);
  }

  async function saveGroupMembershipConfig() {
    if (product?.type !== "Group Membership") {
      return true;
    }

    const includedSaved = await persistIncludedMemberships(
      Number(productId),
      includedMemberships,
      includedMembershipsPreserved,
    );
    if (!includedSaved) {
      dispatch(showError(t("Unable to save included memberships")));
      return false;
    }

    const addonsSaved = await persistAddonMappings(
      Number(productId),
      addonMappings,
      addonMappingsPreserved,
    );
    if (!addonsSaved) {
      dispatch(showError(t("Unable to save add-on membership mappings")));
      return false;
    }

    await loadGroupMembershipConfig();
    return true;
  }



  if (product === undefined) {
    return (
      <Page
        breadcrumbs={[{ label: t("secure.facility.product.product.099"), url: "/admin/product" }]}
        multipleActionDropdownAction={{
          label: t("secure.facility.product.product.114"),
          disabled: true,
          dropdownProps: {
            alignment: "right",
            options: [],
          },
        }}
      >
        <div style={{ height: "50vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
          <div
            style={{
              display: "grid",
              gap: "24px",
              padding: "24px",
              background: "white",
              borderRadius: "6px",
              borderColor: "#D0D5DD",
            }}
          >
            <div style={{ height: "72px" }}>
              <Spin />
            </div>
            <p>{t("secure.facility.product.product.115")}</p>
          </div>
        </div>
      </Page>
    );
  }

  function handleSaveAdjustment(newQuantity: { price: number; cost: number; quantity: number }) {
    setAdjustQuantity(false);
    handleUpdateInventory(
      newQuantity.quantity + product.variants[0].inventory_item.inventory_level.quantity_available,
      newQuantity.price,
      newQuantity.cost,
    );
  }

  function handleDepartments(id: number, type: string) {
    if (id === -1) {
      if (type === "department_id") {
        setProduct(prev => ({ ...prev, [type]: id, category_id: -1, subcategory_id: -1 }));
      } else if (type === "category_id") {
        setProduct(prev => ({ ...prev, [type]: id, subcategory_id: -1 }));
      }
    } else {
      setProduct(prev => ({ ...prev, [type]: id }));
    }
  }

  async function loadAvailableMemberships() {
    const res = await GetMembership(
      {
        search: membershipSearchQuery,
        limit: 500,
        offset: 0,
        archived: false,
        filters: [],
      },
      false,
    );

    if (res.status !== StatusCode.OK) {
      console.error("Error loading memberships");
      return;
    }

    const allMemberships = Array.isArray(res.data) ? res.data : [];
    const filtered = allMemberships.filter(
      membership => !attachedMemberships.some(attached => attached.id === membership.id),
    );

    setAvailableMemberships(filtered);
  }

  async function handleAttachMembership() {
    if (!selectedMembershipId) {
      return;
    };
    const membershipToAttach = availableMemberships?.find(m => m.id === selectedMembershipId);
    const currentMembership = attachedMemberships[0];

    if (currentMembership) {
      const res = await PutMembership({ id: currentMembership.id, product_id: membershipToAttach?.product_id, variant_id: membershipToAttach?.variant_id }, true);

      if (res.status !== StatusCode.OK) {
        dispatch(showError(t("Membership cannot be detached")));
        return;
      }
    }

    const res = await PutMembership({ id: selectedMembershipId, product_id: product.id, variant_id: product.variants?.[0]?.id }, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Membership cannot be attached")));
      return;
    }



    if (membershipToAttach) {
      setAttachedMemberships([membershipToAttach]);
      dispatch(showSuccess(t("Membership attached successfully")));
    }

    setSelectedMembershipId(null);
    setMembershipSearchQuery("");
    closeAttachMembershipModal();
  }

  const productUnit = productUnits.find(unit => unit.id === product.product_unit_id);
  const sectionDivider = <div className="border-gray-200 border-b mx-4 lg:mt-3"></div>;
  const hasNoVariants = (product.variants?.length ?? 0) === 0;
  const canAddVariant =
    Boolean(authStore.user?.permissions?.products_create) &&
    Boolean(facilityStore.facility?.permissions?.products_create) &&
    !product.warehouse;
  const noVariantCalloutActions = {
    ...(selected !== 3
      ? {
          secondary: {
            label: t("Go to Variants"),
            onClick: () => setSelected(3),
          },
        }
      : {}),
    ...(canAddVariant
      ? {
          primary: {
            label: t("Add Variant"),
            onClick: () => history.push("/admin/product/" + productId + "/variant/new"),
          },
        }
      : {}),
  };

  return (
    <Page
      title={productPreserved?.title}
      subtitle={productPreserved?.subtitle}
      breadcrumbs={[{ prefix: true, label: t("secure.facility.product.product.099"), url: "/admin/product" }]}
      multipleActionDropdownAction={{
        label: t("secure.facility.product.product.114"),
        disabled: product === undefined,
        dropdownProps: {
          alignment: "right",
          options: [
            facilityStore?.facility?.is_single_facility
              ? {
                type: "handler",
                label: t("Archive"),
                handler: () => updateArchiveModal({ isOpen: true }),
                disabled: readOnly || !authStore?.user?.permissions?.products_archive,
                icon: "book-open",
              }
              : {
                type: "handler",
                label: t("Remove"),
                handler: () => updateRemoveProductModal({ isOpen: true }),
                disabled: readOnly,
                icon: "trash",
              },
            {
              type: "handler",
              label: t("Duplicate"),
              handler: () => updateDuplicateProductModal({ isOpen: true }),
              disabled: readOnly,
              icon: "copy",
            },
            {
              type: "handler",
              label: t("Forms"),
              handler: () => updateFormsModal({ isOpen: true }),
              disabled: readOnly,
              icon: "pen-to-square",
            },
            {
              type: "handler",
              label: product?.meta?.cannot_be_sold ? t("Set as Can Be Sold") : t("Set as Cannot Be Sold"),
              handler: () => toggleCannotBeSold(),
              disabled: product?.cannot_be_sold,
              icon: product?.meta?.cannot_be_sold ? "check" : "ban",
            },
            {
              type: "handler",
              label: t("Kitchen Locations"),
              handler: () =>
                updateKitchenModal({
                  isOpen: true,
                  kitchen_location_id: product.meta?.kitchen_location_id,
                  kitchen_location_id_2: product.meta?.kitchen_location_id_2,
                  kitchen_location_id_3: product.meta?.kitchen_location_id_3,
                }),
              disabled: false,
              icon: "utensils",
            },
            {
              type: "handler",
              label: t("Print Labels"),
              handler: () => updatePrintLabelModal({ isOpen: true }),
              disabled: false,
              icon: "print",
            },
          ],
        },
      }}
      notificationBarProps={{
        isVisible:
          (unsavedChangesExist(product, productPreserved) ||
            unsavedChangesExist(selectedTaxLine, selectedTaxLineBeforeChanges) ||
            includedMembershipsChanged(includedMemberships, includedMembershipsPreserved) ||
            addonMappingsChanged(addonMappings, addonMappingsPreserved)) &&
          availableOnDateIsValid() &&
          availableUntilDateIsValid(),
        onAction: () => updateProduct(),
        onCancel: () => {
          setProduct(productPreserved);
          setSelectedTaxLine(selectedTaxLineBeforeChanges);
          setAvailableAtDate(productPreserved.available_on ? new Date(productPreserved.available_on) : null);
          setIncludedMemberships(includedMembershipsPreserved);
          setAddonMappings(addonMappingsPreserved);
        },
      }}
    >
      <Page.Section>
        <div style={{ display: "flex", overflowX: "auto", paddingRight: "auto" }}>
          <Tabs tabs={tabs} selected={selected} onSelect={handleTabChange} />
        </div>
      </Page.Section>
      {hasNoVariants ? (
        <Page.Section>
          <Callout
            type="warning"
            title={t("No variants attached")}
            content={t(
              "This product does not have any variants. Create a variant from the Variants & Modifiers tab.",
            )}
            actions={selected !== 3 || canAddVariant ? noVariantCalloutActions : undefined}
          />
        </Page.Section>
      ) : null}
      <br />
      {selected === 0 ? (
        <Page.Section>
          <ProductAvailabilityWarning
            t={t}
            cannotBeSold={product.meta?.cannot_be_sold}
            facilityAccess={product.facility_access}
            toggleFacilityAccess={() => void attachProductToFacility()}
          />

          {/** PRODUCT DETAILS */}
          <Card title={t("secure.facility.product.product.009")}>
            <Card.Section>
              <FormLayout>
                <FormLayout.Group>
                  <Input
                    id="title"
                    value={product?.title || ""}
                    onChange={e => handleChangeEventInput(e, product, setProduct)}
                    label={t("secure.facility.product.product.010")}
                    placeholder={t("secure.facility.product.product.011")}
                    readOnly={readOnly}
                    error={!validated("title") && attempted}
                  />
                </FormLayout.Group>
                <FormLayout.Group>
                  <Input
                    id="subtitle"
                    value={product?.subtitle || ""}
                    label={t("secure.facility.product.product.013")}
                    onChange={e => handleChangeEventInput(e, product, setProduct)}
                    placeholder={t("secure.facility.product.product.014")}
                    error={!validated("subtitle") && attempted}
                  />
                  <Input
                    id="preferred_title"
                    value={product?.preferred_title || ""}
                    label={t("secure.facility.product.product.015")}
                    onChange={e => handleChangeEventInput(e, product, setProduct)}
                    placeholder={t("secure.facility.product.product.016")}
                  />
                </FormLayout.Group>
                <FormLayout.Group>
                  <Select
                    label={t("secure.facility.product.product.035")}
                    onChange={(value: number) => handleDepartments(value, "department_id")}
                    // onChange={(value: number) => setProduct(prev => ({ ...prev, department_id: value }))}
                    defaultValue={product.department_id}
                    disabled={readOnly}
                    error={!validated("department_id") && attempted}
                    placeholder={t("secure.facility.product.product.035")}
                  >
                    <Select.Option value={-1}>None</Select.Option>
                    {departments
                      .filter(department => department.type === "department")
                      .map(department => (
                        <Select.Option key={department.id} value={department.id} name={department.title}>
                          {department.title}
                        </Select.Option>
                      ))}
                  </Select>

                  <Select
                    label={t("secure.facility.product.product.036")}
                    onChange={(value: number) => setProduct(prev => ({ ...prev, category_id: value }))}
                    defaultValue={product?.category_id}
                    disabled={readOnly}
                    error={!validated("category_id") && attempted}
                    placeholder={t("secure.facility.product.product.036")}
                  >
                    <Select.Option value={-1}>{t("secure.facility.product.product.149")}</Select.Option>
                    {departments
                      .filter(
                        department => department.type == "category" && department.parent_id == product.department_id,
                      )
                      .map(category => (
                        <Select.Option key={category.id} value={category.id} name={category.title}>
                          {category.title}
                        </Select.Option>
                      ))}
                  </Select>
                  <Select
                    label={t("secure.facility.product.product.037")}
                    onChange={(value: number) => setProduct(prev => ({ ...prev, subcategory_id: value }))}
                    defaultValue={product.subcategory_id}
                    disabled={readOnly}
                    error={!validated("subcategory_id") && attempted}
                    placeholder={t("secure.facility.product.product.037")}
                  >
                    <Select.Option value={-1}>{t("secure.facility.product.product.149")}</Select.Option>
                    {departments
                      .filter(
                        department => department.type == "subcategory" && department.parent_id == product.category_id,
                      )
                      .map(subcategory => (
                        <Select.Option key={subcategory.id} value={subcategory.id} name={subcategory.title}>
                          {subcategory.title}
                        </Select.Option>
                      ))}
                  </Select>
                </FormLayout.Group>
                <FormLayout.Group>
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    <TextEditor
                      markdownText={product?.description || ""}
                      markdownTextOnChange={(description: string) => setProduct(prev => ({ ...prev, description }))}
                      label={t("secure.facility.product.product.012")}
                    />
                    {/* <div className="product-description-subtext">Description is used to describe the product.</div> */}
                  </div>
                </FormLayout.Group>

                <FormLayout.Group>
                  <ButtonNew
                    type="text"
                    size="small"
                    onClick={() => {
                      setDetailsCard(prev => ({
                        ...prev,
                        internal_description_visible: !prev.internal_description_visible,
                      }));
                    }}
                  >
                    {!detailsCard.internal_description_visible ? (
                      <FontAwesomeIcon icon={faPlus} />
                    ) : (
                      <FontAwesomeIcon icon={faMinus} />
                    )}
                    <div style={{ marginLeft: "5px" }}>{t("secure.facility.product.product.096")}</div>
                  </ButtonNew>
                </FormLayout.Group>
                {detailsCard.internal_description_visible ||
                  productPreserved?.internal_description ||
                  product?.internal_description ? (
                  <FormLayout.Group>
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                      <TextEditor
                        markdownText={product.internal_description || ""}
                        markdownTextOnChange={internal_description =>
                          setProduct(prev => ({
                            ...prev,
                            internal_description: internal_description === "" ? null : internal_description,
                          }))
                        }
                        label={t("secure.facility.product.product.097")}
                        readOnly={
                          !authStore.user?.permissions?.products_edit ||
                          !facilityStore.facility?.permissions.products_edit
                        }
                      />
                      {/* <div className="product-description-subtext">This is a hint text to help the user.</div> */}
                    </div>
                  </FormLayout.Group>
                ) : null}
              </FormLayout>
            </Card.Section>
            {facilityStore?.loyaltyProgram && product?.variants?.length === 1 ? (
              <Card.Section title={t("Loyalty Points Override")}>
                <Input
                  value={product?.variants[0]?.meta?.loyalty_points_override ?? ""}
                  label={t("Points Earned When Purchased")}
                  id="loyalty_points_override"
                  //placeholder={t("Placeholder")}
                  tooltipText={t("This field allows you to set a specific number of loyalty points earned for purchasing this product. If no value is set, the default loyalty points set in the Loyalty Program section will be used.")}
                  type="number"
                  onChange={e =>
                    setProduct(prevState => ({
                      ...prevState,
                      variants: prevState?.variants?.map(variant => ({
                        ...variant,
                        meta: { ...variant?.meta, loyalty_points_override: e.target.value },
                      })),
                    }))
                  }
                  readOnly={
                    !authStore.user?.permissions?.products_edit || !facilityStore.facility?.permissions.products_edit
                  }
                />
              </Card.Section>
            ) : null}
          </Card>
        </Page.Section>
      ) : null}

      {/**
       * PRICING
       * 1 Variant
       */}
      {selected === 1 ? (
        <Page.Section>
          {product.variants.length === 1 ? (
            <Card
              title={
                <div>
                  {t("Pricing")}
                  {product.type?.toLowerCase() === "combo" && product.dynamic_combo !== true &&
                    <OverlayTrigger
                      placement="auto"
                      overlay={
                        <Tooltip id={"toggle_tool_tip_text"}>
                          {t("Pricing & taxes are determined by the combo products")}
                        </Tooltip>
                      }
                    >
                      <FontAwesomeIcon
                        style={{ marginLeft: "0.5rem" }}
                        icon={faQuestionCircle}
                        size="xs"
                        className="ui-toggle-tooltip"
                      />
                    </OverlayTrigger>
                  }


                </div>
              }
              titleActions={
                authStore.user.permissions?.products_edit &&
                  facilityStore.facility?.permissions?.products_edit &&
                  !product?.warehouse
                  ? [
                    {
                      content: t("Add Product Option"),
                      action: () => history.push("/admin/product/" + productId + "/variant/new"),
                    },
                  ]
                  : undefined
              }
            >
              <Card.Section>
                <FormLayout>
                  <FormLayout.Group>
                    <Input
                      id="retail_price"
                      value={product.price || ""}
                      type="number"
                      prefix="$"
                      label={t("Retail Price")}
                      onChange={e => handlePriceChange(Number(e.target.value))}
                      placeholder="0.00"
                      error={!validated("price") && attempted}
                      disabled={
                        !authStore.user.permissions?.products_edit ||
                        !authStore.user.permissions?.products_edit_price ||
                        product.price_locked ||
                        (product.type?.toLowerCase() === "combo" && product.dynamic_combo !== true)
                      }
                    />
                    <Input
                      id="cost"
                      value={product.cost || ""}
                      type="number"
                      prefix="$"
                      label={t("Cost")}
                      onChange={e => handleCostChange(Number(e.target.value))}
                      placeholder="0.00"
                      error={!validated("cost") && attempted}
                      disabled={
                        !authStore.user.permissions?.products_edit ||
                        !authStore.user.permissions?.products_edit_cost ||
                        (product.type?.toLowerCase() === "combo" && product.dynamic_combo !== true)
                      }
                    />
                    <Input
                      id="markup"
                      value={Number(roundNumber(markup, 2)) || ""}
                      onChange={e => handleMarkupChange(Number(e.target.value))}
                      type="number"
                      prefix="%"
                      label={t("Markup")}
                      placeholder="0.00"
                      disabled={
                        !authStore.user.permissions?.products_edit ||
                        !authStore.user.permissions?.products_edit_price ||
                        product.price_locked ||
                        (product.type?.toLowerCase() === "combo" && product.dynamic_combo !== true)
                      }
                    />
                  </FormLayout.Group>
                </FormLayout>
              </Card.Section>
              <Card.Section
                title={t("Price after tax")}
                sectionTitleActions={[
                  {
                    content: <FontAwesomeIcon icon={hidePriceAfterTax ? faChevronUp : faChevronDown} />,
                    action: () => {
                      setHidePriceAfterTax(!hidePriceAfterTax);
                    },
                  },
                ]}
              >
                {!hidePriceAfterTax ? (
                  <FormLayout>
                    <FormLayout.Group>
                      <SelectNew
                        id="tax-lines-select"
                        label={t("Tax Lines")}
                        placeholder={(selectedTaxLine?.length ?? 0) === 0 ? t("No tax") : getTaxNames()}
                        multiValue={taxLineSelectValue(selectedTaxLine)}
                        options={taxLineSelectOptions(taxlines, t("No tax"))}
                        onChange={value => toggleTaxLines(value)}
                        disabled={product.type?.toLowerCase() === "combo" && product.dynamic_combo !== true}

                      />
                      <Input
                        id="price_after_tax"
                        value={priceAfterTax || ""}
                        type={"number"}
                        prefix="$"
                        label={t("Price after tax")}
                        placeholder="0.00"
                        error={!validated("price") && attempted}
                        onChange={e => handlePriceAfterTaxChange(Number(e.target.value))}
                        disabled={
                          !authStore.user.permissions?.products_edit ||
                          !authStore.user.permissions?.products_edit_price ||
                          product.price_locked ||
                          (product.type?.toLowerCase() === "combo" && product.dynamic_combo !== true)
                        }
                      />
                    </FormLayout.Group>
                  </FormLayout>
                ) : null}
              </Card.Section>
            </Card>
          ) : product.variants.length > 1 ? (
            <Card title={t("secure.facility.product.product.017")}>
              <div
                className="variant-pricing-alert-container"
                style={{ marginInline: "20px", marginBlock: "10px", display: "flex" }}
              >
                <FontAwesomeIcon icon={faCircleExclamation} style={{ marginTop: "5px" }} />
                <div className="flex flex-col">
                  <div>This product has multiple variants</div>
                  <div style={{ fontWeight: "normal", marginBottom: "10px" }}>
                    To see prices by variant go to the Variants & Modifiers tab.
                  </div>

                  <div onClick={() => setSelected(3)} className="cursor-pointer">
                    Go to Variants & Modifiers <FontAwesomeIcon icon={faArrowRight} />
                  </div>
                </div>
                <br />
              </div>
              <br />
            </Card>
          ) : null}

          {/* Combo 1 Variant */}
          {product.type?.toLowerCase() === "combo" && product.variants.length === 1 && (
            <Card
              title={
                <span className="flex flex-row ">
                  {t("Combo Products")}
                  <div
                    className="product-inventory-level-table-quantity"
                    style={{ marginLeft: "10px", paddingInline: "5px" }}
                  >
                    {comboProducts.length ?? 0}
                    &nbsp; {t("Products")}
                  </div>
                </span>
              }
              subtitle={t("Manage your Combo")}
              titleActions={[
                {
                  content: (
                    <div>
                      <FontAwesomeIcon icon={faPlus} /> {t("secure.facility.product.product.042")}
                    </div>
                  ),
                  action: () => updateNewComboProductModal({ isOpen: true }),
                  disabled: !authStore.user?.permissions?.products_edit,
                },
              ]}
            >
              <Card.Section>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px" }}>
                  <Checkbox
                    size="small"
                    label={t("secure.facility.product.edit.product_edit.006")}
                    checked={product?.dynamic_combo}
                    onChange={() => setProduct(prev => ({ ...prev, dynamic_combo: !prev.dynamic_combo }))}
                  />
                </div>
              </Card.Section>
              {product.dynamic_combo && (
                <Card.Section>
                  <div className="flex flex-row" style={{ display: "flex", gap: "20px" }}>
                    <Input
                      id="combo_sales_units"
                      type={"number"}
                      label={t("Sales Units")}
                      onChange={e => setProduct(prev => ({ ...prev, dynamic_combo_quantity: Number(e.target.value) }))}
                      placeholder={"0"}
                      value={product.dynamic_combo_quantity || ""}
                    />
                    <Input
                      id="combo_price"
                      value={product.price ?? ""}
                      label={t("Price")}
                      onChange={e => setProduct(prev => ({ ...prev, price: Number(e.target.value) }))}
                      prefix={"$"}
                      placeholder={"0.00"}
                    />
                    <Input
                      id="combo_cost"
                      value={product.cost ?? ""}
                      label={t("Cost")}
                      onChange={e => setProduct(prev => ({ ...prev, cost: Number(e.target.value) }))}
                      prefix={"$"}
                      placeholder={"0.00"}
                    />
                  </div>
                </Card.Section>
              )}
              <Card.Section>
                <div style={{ overflowX: "auto" }}>
                  <ActionBar
                    visible={selectedComboVariant?.length > 0}
                    selectedItemIds={selectedComboVariant}
                    clearSelectedItemIds={() => setSelectedComboVariant([])}
                    itemSingularTitle={t("Combo Product")}
                    itemPluralTitle={t("Combo Products")}
                    primaryActions={[
                      {
                        icon: faTrash,
                        title: t("Remove"),
                        action: () => {
                          for (const id of selectedComboVariant) {
                            void removeProductFromCombo([comboProducts.find(combo => combo.id === id)]);
                          }
                          setSelectedComboVariant([]);
                        },
                      },
                    ]}
                  >
                    <DataTable
                      columns={[
                        {
                          label: t("Product Title"),
                          content: (
                            <span className="flex flex-row" style={{ fontWeight: "normal" }}>
                              <Checkbox
                                size="medium"
                                id="combo_product_select_all"
                                checked={
                                  selectedComboVariant.length === comboProducts.length && comboProducts.length > 0
                                }
                                onChange={e => {
                                  if (e.target.checked) {
                                    setSelectedComboVariant(comboProducts.map(combo => combo.id));
                                  } else {
                                    setSelectedComboVariant([]);
                                  }
                                }}
                              />
                              {t("Product Title")}
                            </span>
                          ),
                          width: "25%",
                        },
                        { label: t("Variant"), width: "20%" },
                        { label: t("Quantity"), width: "10%" },
                        { label: t("Price"), width: "10%" },
                        { label: t("client_side.product.product.015"), width: "10%" },
                        { label: t("Type"), width: "10%" },
                        { label: t("SKU"), width: "15%" },
                        { label: t("Actions"), width: "5%" },
                        { label: "", width: "5%" },
                      ]}
                    >
                      {comboProducts?.map((combo: TComboVariants) => (
                        <tr key={combo.id}>
                          <td>
                            <span className="flex flex-row algin-center">
                              <Checkbox
                                id="combo_product"
                                size="medium"
                                checked={selectedComboVariant.includes(combo.id)}
                                onChange={e => {
                                  if (e.target.checked) {
                                    setSelectedComboVariant(prev => [...prev, combo.id]);
                                  } else {
                                    setSelectedComboVariant(prev => prev.filter(id => id !== combo.id));
                                  }
                                }}
                              />
                              <p>{combo.product.title}</p>
                            </span>
                          </td>
                          <td>{combo.title !== combo.product.title ? <p>{combo.title}</p> : null}</td>
                          <td>{combo.combo_pivot.quantity}</td>
                          <td>
                            {combo?.combo_pivot.price
                              ? displayCurrency("cad", parseFloat(String(combo?.combo_pivot.price)))
                              : "$0.00"}
                          </td>
                          <td>
                            {combo?.combo_pivot.cost
                              ? displayCurrency("cad", parseFloat(String(combo?.combo_pivot.cost)))
                              : "$0.00"}
                          </td>
                          <td>
                            <Badge>{combo.product.type}</Badge>
                          </td>
                          <td>{combo.sku}</td>
                          <td>
                            <ButtonNew
                              type="text"
                              onClick={() => void removeProductFromCombo([combo])}
                              disabled={!authStore.user?.permissions?.products_edit}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </ButtonNew>
                          </td>
                          <td>
                            <ButtonNew
                              type="text"
                              onClick={() =>
                                updateEditComboProductModal({
                                  isOpen: true,
                                  selectedComboVariant: combo,
                                })
                              }
                              disabled={!authStore.user?.permissions?.products_edit}
                            >
                              <FontAwesomeIcon icon={faPenToSquare} />
                            </ButtonNew>
                          </td>
                        </tr>
                      ))}
                    </DataTable>
                  </ActionBar>
                </div>
              </Card.Section>
            </Card>
          )}
        </Page.Section>
      ) : null}

      {selected === 2 && (
        <Page.Section>
          <Card
            title={t("Product Logistics")}
            subtitle={t("Configure vendor, unit, and fulfillment settings for this product.")}
          >
            {sectionDivider}
            {/** LOGISTICS */}
            <Card.Section>
              <FormLayout>
                <FormLayout.Group>
                  <Select
                    label={t("Product Types")}
                    placeholder={t("Search Product Types..")}
                    className={`flex justify-center align-center w-full h-10 position-relative z-20 text-black font-medium appearance-none border-none focus:outline-none placeholder-gray-200`}
                    onSearch={(query: string) => logisticsCardSearch("productTypeSearch", query)}
                    onChange={(value: string) => setProduct(prev => ({ ...prev, type: value }))}
                    searchValue={logisticsCard.productTypeSearch || ""}
                    allowClear={
                      authStore.user?.permissions?.products_edit || facilityStore.facility?.permissions.products_edit
                    }
                    disabled={readOnly}
                    showSearch
                    showDropDownOnFocus
                    hideDropdown
                    error={!validated("type") && attempted}
                  >
                    {productTypes
                      .filter(val => val.title?.toLowerCase().includes(logisticsCard.productTypeSearch?.toLowerCase()))
                      .map(type => {
                        return (
                          <Select.Option key={type.id} value={type.title} name={type.title}>
                            {type.title}
                          </Select.Option>
                        );
                      })}
                  </Select>
                  <Select
                    label={t("Vendor")}
                    onChange={(value: number) => setProduct(prev => ({ ...prev, vendor_id: value }))}
                    className={`flex justify-center align-center w-full h-10 position-relative z-20 text-black font-medium appearance-none border-none focus:outline-none placeholder-gray-200`}
                    onSearch={(query: string) => logisticsCardSearch("vendorSearch", query)}
                    placeholder={t("secure.facility.product.product.121")}
                    searchValue={logisticsCard.vendorSearch || ""}
                    defaultValue={product?.vendor_id}
                    disabled={readOnly}
                    error={!validated("vendor_id") && attempted}
                    showSearch
                    showDropDownOnFocus
                    hideDropdown
                    allowClear={
                      authStore.user?.permissions?.products_edit || facilityStore.facility?.permissions.products_edit
                    }
                  >
                    <Select.Option key={"none"} value={undefined} name="none">
                      {t("secure.facility.product.product.149")}
                    </Select.Option>
                    {vendors
                      .filter(val => val.title?.toLowerCase().includes(logisticsCard.vendorSearch?.toLowerCase()))
                      .map((vendor, index: number) => {
                        return (
                          <Select.Option key={index} value={vendor.id} name={vendor.title}>
                            {vendor.title}
                          </Select.Option>
                        );
                      })}
                  </Select>
                </FormLayout.Group>
                <FormLayout.Group alignStart={comboSelected}>
                  {comboSelected ? (
                    <Input
                      id="combo_accounting_references"
                      label={t("secure.facility.product.product.028")}
                      value={comboAccountingReferenceLabels.join(", ")}
                      placeholder={t("Add products to the combo")}
                      helpText={t("Determined by the products in the combo")}
                      disabled
                    />
                  ) : (
                    <Select
                      label={t("secure.facility.product.product.028")}
                      placeholder={t("secure.facility.product.product.029")}
                      className={`flex justify-center align-center w-full h-10 position-relative z-20 text-black font-medium appearance-none border-none focus:outline-none placeholder-gray-200`}
                      onSearch={(query: string) => logisticsCardSearch("accountRefSearch", query)}
                      onChange={(value: any) => setProduct(prev => ({ ...prev, accounting_reference_id: value }))}
                      searchValue={logisticsCard.accountRefSearch || ""}
                      showDropDownOnFocus={false}
                      hideDropdown
                      showSearch
                      disabled={readOnly}
                      allowClear={
                        authStore.user?.permissions?.products_edit || facilityStore.facility?.permissions.products_edit
                      }
                      error={!validated("accounting_reference_id") && attempted}
                    >
                      <div key={"wrap"}>
                        {accountingReferences
                          .filter(accountingReference =>
                            accountingReferenceMatchesQuery(
                              accountingReference.title ?? "",
                              accountingReference.reference_number,
                              logisticsCard.accountRefSearch ?? "",
                            ),
                          )
                          .map((reference: TAccountingReference) => {
                            const label = formatAccountingReferenceLabel(
                              reference.title,
                              reference.reference_number,
                            );
                            return (
                              <div key={reference.id}>
                                {logisticsCard.accountRefSearch !== "" ? (
                                  <Select.Option key={reference.id} value={reference.id} name={label}>
                                    {label}
                                  </Select.Option>
                                ) : null}
                              </div>
                            );
                          })}
                      </div>
                    </Select>
                  )}
                  <Select
                    label={t("Product Unit")}
                    onChange={(value: number) =>
                      setProduct(prev => ({
                        ...prev,
                        product_unit_id: value,
                      }))
                    }
                    className={`flex justify-center align-center w-full h-10 position-relative z-20 text-black font-medium appearance-none border-none focus:outline-none placeholder-gray-200`}
                    onSearch={(query: string) => logisticsCardSearch("productUnitSearch", query)}
                    placeholder={t("secure.facility.product.product.123")}
                    searchValue={logisticsCard.productUnitSearch || ""}
                    disabled={readOnly}
                    showSearch
                    showDropDownOnFocus
                    hideDropdown
                    allowClear={
                      authStore.user?.permissions?.products_edit || facilityStore.facility?.permissions.products_edit
                    }
                  >
                    <Select.Option key={"none"} value={undefined} name="none">
                      {t("secure.facility.product.product.149")}
                    </Select.Option>
                    {productUnits
                      .filter(
                        val =>
                          val.name?.toLowerCase().includes(logisticsCard.productUnitSearch?.toLowerCase()) ||
                          val.abbreviation?.toLowerCase().includes(logisticsCard.productUnitSearch?.toLowerCase()),
                      )
                      .map((unit, index: number) => {
                        return (
                          <Select.Option key={index} value={unit.id} name={unit.name}>
                            {`${unit.name}${unit.abbreviation ? ` (${unit.abbreviation})` : ""}`}
                          </Select.Option>
                        );
                      })}
                  </Select>
                </FormLayout.Group>
                <div className="ml-8 flex flex-col gap-2">
                  <ButtonNew
                    type="tertiary"
                    className="ml-8 px-0"
                    onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
                  >
                    {t("Advanced Settings")}
                    <FontAwesomeIcon className="ml-2" icon={showAdvancedSettings ? faChevronDown : faChevronUp} />
                  </ButtonNew>
                  {showAdvancedSettings && (
                    <>
                      <Toggle
                        id="customer_required"
                        checked={product.customer_required}
                        onChange={e => handleChangeEventInput(e, product, setProduct)}
                        labelRight={t("Customer Required")}
                        labelSupport={t(
                          "Requires a customer to be selected before completing checkout in Point of Sale.",
                        )}
                        disabled={!authStore.user?.permissions?.products_edit}
                      />
                      <Toggle
                        id="shipping_required"
                        checked={product.shipping_required}
                        onChange={e => handleChangeEventInput(e, product, setProduct)}
                        labelRight={t("Shipping Required")}
                        labelSupport={t("Product must be shipped to a customer or another location.")}
                        disabled={!authStore.user?.permissions?.products_edit}
                      />
                      <Toggle
                        id="fulfillment_required"
                        checked={product.fulfillment_required}
                        onChange={e => handleChangeEventInput(e, product, setProduct)}
                        labelRight={t("Fulfillment Required")}
                        labelSupport={t("Marks this product for fulfillment via a third-party service or delivery.")}
                        disabled={!authStore.user?.permissions?.products_edit}
                      />
                      <Toggle
                        id="tax_exempt"
                        checked={product.tax_exempt}
                        onChange={e => handleChangeEventInput(e, product, setProduct)}
                        labelRight={t("Eligible for Tax Exemption")}
                        labelSupport={t("Tax exemptions can be applied to this product.")}
                        disabled={!authStore.user?.permissions?.products_edit}
                      />
                      <Toggle
                        id="dynamic_price"
                        checked={product.dynamic_price}
                        onChange={e => handleChangeEventInput(e, product, setProduct)}
                        labelRight={t("Dynamic Price")}
                        labelSupport={t("The price of this product will be set when it's added to the cart.")}
                        disabled={!authStore.user?.permissions?.products_edit}
                      />
                    </>
                  )}
                </div>
              </FormLayout>
            </Card.Section>
          </Card>

          <GiftCardProductSettingsCard
            product={product}
            setProduct={setProduct}
            showFacilityRestriction={showGiftCardFacilityRestriction}
            disabled={!authStore.user?.permissions?.products_edit}
            onToggleChange={handleChangeEventInput}
          />

          {/**
           * PRODUCT TICKETS
           * 1 Variant
           */}
          {product.type?.toLowerCase() === "ticket" && product.variants.length === 1 && (
            <Card
              title={t("Tickets")}
              titleActions={[
                {
                  content: t("secure.facility.product.product.044"),
                  action: () => updateAddTicketModal({ isOpen: true }),
                  disabled: !authStore.user?.permissions?.products_edit,
                },
              ]}
            >
              <Card.Section>
                <FormLayout>
                  <FormLayout.Group>
                    <div className="ui-checkbox-group">
                      {product.variants[0].tickets.map(ticket => (
                        <div key={ticket.id} className="ui-checkbox-group-item">
                          <div className="ui-checkbox-group-item-content">
                            <div className="ui-checkbox-group-item-text">
                              <p className="text-sm text-medium text-gray-700">{ticket.title}</p>
                              {ticket.subtitle && (
                                <p className="text-xs text-medium text-gray-500">{ticket.subtitle}</p>
                              )}
                              <p className="text-xs text-regular text-gray-500">{ticket.included}</p>
                            </div>
                          </div>
                          <div className="flex flex-col">
                            <ButtonNew
                              type="text"
                              size="small"
                              onClick={() => void removeTicket(ticket)}
                              style={{ alignSelf: "flex-end", padding: "0px", marginBottom: "-5px" }}
                            >
                              {t("secure.facility.product.variant.013")}
                            </ButtonNew>
                            <div style={{ padding: "0px" }} className="flex">
                              <Input
                                onChange={e => productTicketQuantityOnChange(ticket.id, e.target.value)}
                                value={ticket.quantity}
                                type="number"
                                label=""
                                error={Number(ticket.quantity) < 1 && attempted}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </FormLayout.Group>
                </FormLayout>
              </Card.Section>
            </Card>
          )}

          {/* Product Inventory*/}
          <Card
            title={
              <span className="flex flex-row ">
                {t("Product Inventory")}
                {product.variants.length === 1 && product.track_inventory && (
                  <Badge size="small" type="primary" className="ml-2">
                    {product?.variants[0]?.inventory_item?.inventory_level?.quantity_available ?? 0}
                    &nbsp; {t(" in stock")}
                  </Badge>
                )}
              </span>
            }
            subtitle={t("Receiving, storing, and tracking products in a warehouse or facility to ensure availability.")}
            collapsable={true}
            defaultCollapsed={false}
          >
            {sectionDivider}
            <Card.Section>
              <FormLayout>
                <FormLayout.Group>
                  <Toggle
                    id="track_inventory"
                    checked={product.track_inventory}
                    onChange={e => handleChangeEventInput(e, product, setProduct)}
                    labelRight={t("Track Inventory")}
                    labelSupport={t("Manage and maintain accurate stock levels and procurement.")}
                    disabled={!authStore.user?.permissions?.products_edit}
                  />
                </FormLayout.Group>

                {product.variants.length === 1 && (
                  <>
                    <FormLayout.Group>
                      <Input
                        id="barcode"
                        value={product.barcode || ""}
                        onChange={e => handleChangeEventInput(e, product, setProduct)}
                        label={t("Barcode")}
                        disabled={readOnly || product?.warehouse_product_id !== null}
                      />
                      <span className="flex flex-row items-center gap-4">
                        <div style={{ width: "100%" }}>
                          <Input
                            id="sku"
                            value={product.sku || ""}
                            onChange={e => handleChangeEventInput(e, product, setProduct)}
                            label={t("SKU")}
                            disabled={readOnly || product?.warehouse_product_id !== null}
                            placeholder={t("SKU")}
                            trailingButtons={[
                              <ButtonNew
                                key="generate-sku"
                                onClick={() => {
                                  setProduct(prev => ({
                                    ...prev,
                                    sku: generateSKU(),
                                  }));
                                }}
                                disabled={readOnly || product?.warehouse_product_id !== null}
                              >
                                <div
                                  className="flex flex-row"
                                  style={{ alignItems: "center", gap: "5px", width: "140px" }}
                                >
                                  <FontAwesomeIcon icon={faRotate} />
                                  <div className="flex flex-row">{t("Generate SKU")}</div>
                                </div>
                              </ButtonNew>,
                            ]}
                          />
                        </div>
                      </span>
                    </FormLayout.Group>
                  </>
                )}
              </FormLayout>

              {product.variants.length > 1 && product.track_inventory && (
                <>
                  <DataTable
                    columns={[
                      {
                        id: "variant",
                        label: "Variant",
                      },
                      {
                        id: "qty",
                        label: "Quantity",
                      },
                      {
                        id: "actions",
                        label: "Actions",
                        justify: "right",
                      },
                    ]}
                  >
                    {product.variants.map(v => (
                      <tr key={v.id}>
                        <td>{v.title}</td>
                        <td>
                          {v.inventory_item.inventory_level.quantity_available ?? 0}{" "}
                          {productUnit?.abbreviation ?? t("units")}
                        </td>
                        <td align="right">
                          <ButtonNew
                            type="tertiary"
                            size="xsmall"
                            onClick={() => history.push(`/admin/product/${product.id}/variant/${v.id}?tab=logistics`)}
                          >
                            {t("Adjust")}
                            <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="ml-2" />
                          </ButtonNew>
                        </td>
                      </tr>
                    ))}
                  </DataTable>
                </>
              )}
            </Card.Section>

            {product.variants.length === 1 && product.track_inventory && (
              <>
                <div className="border-gray-200 border-b mx-4"></div>
                <Card.Section
                  title={t("Inventory History")}
                  subtitle={t("Track and adjust past receivings and quantity changes.")}
                  sectionTitleActions={[
                    {
                      content: (
                        <>
                          {t("Adjust Quantity")}
                          <FontAwesomeIcon icon={faSlidersSimple} className="ml-2" />
                        </>
                      ),
                      action: () => setAdjustQuantity(true),
                      disabled: product?.warehouse_product_id !== null,
                    },
                  ]}
                >
                  <div className="border-gray-200 border-b mb-4"></div>

                  <InventoryHistoryTable
                    variant={product.variants[0]}
                    adjustQuantity={adjustQuantity}
                    onSaveAdjustment={handleSaveAdjustment}
                    onCancelAdjustment={() => setAdjustQuantity(false)}
                    unit={productUnit?.abbreviation ?? t("units")}
                  />

                  <div className="flex justify-center">
                    <ButtonNew type="tertiary" onClick={displayInventoryLog}>
                      {t("View more")}
                    </ButtonNew>
                  </div>

                  <div className="border-gray-200 border-b my-4"></div>
                  <p className="text-lg text-semibold mb-3">{t("Register Inventory Transfers")}</p>
                  <RegisterInventoryTransferTable
                    variantId={product.variants[0]?.id}
                    unit={productUnit?.abbreviation ?? t("units")}
                  />
                </Card.Section>
              </>
            )}
          </Card>

          {/* Warehouse Inventory*/}
          <Card
            collapsable={true}
            defaultCollapsed={false}
            title={t("Warehouse Inventory")}
            subtitle={t("Modify and manage warehouse products and its inventory.")}
          >
            {sectionDivider}
            <Card.Section>
              <>
                <Toggle
                  id="warehouse"
                  checked={product.warehouse}
                  onChange={e => handleChangeEventInput(e, product, setProduct)}
                  labelRight={t("Warehouse Product")}
                  labelSupport={t("This product is being stored in a warehouse")}
                  disabled={
                    !authStore.user?.permissions?.products_edit ||
                    (!product.warehouse && product.variants.length > 1)
                  }
                />
                <div className="mt-6"></div>
              </>
              <FormLayout>
                {!product?.warehouse && (
                  <>
                    <FormLayout.Group>
                      <Select
                        label={t("Attach Warehouse Product")}
                        onChange={(_, product) => {
                          ReactDOM.unstable_batchedUpdates(() => {
                            setWarehouseProductSearch("");
                            setProduct(prev => ({
                              ...prev,
                              warehouse_products: prev?.warehouse_products
                                ? [...prev.warehouse_products, { ...product, inventory_units: "" }]
                                : [{ ...product, inventory_units: "" }],
                            }));
                          });
                        }}
                        onSearch={(query: string) => setWarehouseProductSearch(query)}
                        searchValue={warehouseProductSearch || ""}
                        disabled={readOnly}
                        showSearch
                        placeholder={t("Search Warehouse Products...")}
                        showDropDownOnFocus
                        hideDropdown
                        allowClear={
                          authStore.user?.permissions?.products_edit ||
                          facilityStore.facility?.permissions.products_edit
                        }
                        noData={
                          <div className="flex items-center p-2 text-black">
                            <FontAwesomeIcon icon={faExclamationCircle} fixedWidth size="1x" />
                            <span className="ml-3 italic">{t("No warehouse products found...")}</span>
                          </div>
                        }
                      >
                        {warehouseProducts
                          ?.filter(
                            warehouseProduct =>
                              warehouseProduct?.id != product?.id &&
                              !product?.warehouse_products?.some(product => product?.id === warehouseProduct?.id),
                          )
                          ?.map((product, index: number) => {
                            return (
                              <Select.Option key={index} value={product.id} extraValues={product}>
                                {`${String(product.title)}${product.subtitle ? ` - ${String(product.subtitle)}` : ""}`}
                              </Select.Option>
                            );
                          })}
                      </Select>
                    </FormLayout.Group>
                    <div className="ml-8">
                      <DataTable
                        columns={
                          product.variants.length === 1
                            ? [
                              { label: t("Warehouse Product"), width: "40%" },
                              { label: t("Inventory Units"), width: "20%" },
                              { label: "", width: "40%" },
                            ]
                            : [
                              { label: t("Warehouse Product"), width: "40%" },
                              { label: "", width: "60%" },
                            ]
                        }
                      >
                        {product?.warehouse_products?.map((warehouseProduct, index) => {
                          return (
                            <tr key={index}>
                              <td>
                                {warehouseProduct?.preferred_title
                                  ? warehouseProduct?.preferred_title
                                  : warehouseProduct?.title}
                              </td>
                              {product.variants.length === 1 && (
                                <td>
                                  <Input
                                    labelHidden
                                    placeholder={t("Inventory units")}
                                    value={warehouseProduct?.inventory_units ?? ""}
                                    onChange={e => onInventoryUnitsChange(e, index)}
                                    type="number"
                                    suffix={
                                      productUnits.find(unit => unit.id === warehouseProduct.product_unit_id)
                                        ?.abbreviation
                                    }
                                  />
                                </td>
                              )}
                              <td>
                                <div className="flex">
                                  <FontAwesomeIcon
                                    icon={faTrash}
                                    className="cursor-pointer ml-auto"
                                    onClick={() =>
                                      setProduct(prev => ({
                                        ...prev,
                                        warehouse_products: prev?.warehouse_products?.filter(
                                          prod => prod?.id !== warehouseProduct?.id,
                                        ),
                                      }))
                                    }
                                  />
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </DataTable>
                    </div>
                  </>
                )}
              </FormLayout>
            </Card.Section>
          </Card>
          {/* INVENTORY BY FACILITY */}
          {product.track_inventory && (
            <Card
              title={t("Inventory by Facility")}
              collapsable={true}
              defaultCollapsed={false}
              subtitle={t("Manage inventory and availability for this product across facilities.")}
              titleActions={[
                {
                  content: showAllFacilityInventories ? t("Hide Secondary Locations") : t("View All Locations"),
                  action: () => setShowAllFacilityInventories(!showAllFacilityInventories),
                  disabled: false,
                },
              ]}
            >
              {sectionDivider}
              <Card.Section>
                <div className="product-edit-inventory-table">
                  <FacilityInventoryTable
                    mainFacilityId={facilityStore.facility?.id || 0}
                    facilities={facilityStore.facilities}
                    variants={product.variants}
                    showAllFacilities={showAllFacilityInventories}
                  />
                </div>
              </Card.Section>
            </Card>
          )}

          {(product.type === "Membership" || product.type === "Membership Add On") && product.variants.length === 1 ? (<Card
            title={t("Memberships Attached to This Product")}
            subtitle={t("This product is attached to the following memberships")}
            titleActions={[]}
          >
            {sectionDivider}
            <Card.Section>
              {membershipState.loading ? (
                <div className="flex justify-center py-8">
                  <Spin />
                </div>
              ) : (
                <DataTable
                  columns={[
                    { label: t("Membership Title"), width: "35%" },
                    { label: t("Subtitle"), width: "25%" },
                    { label: t("Type"), width: "15%" },
                    { label: t("Variant"), width: "15%" },
                    { label: t("Actions"), width: "10%" },
                  ]}
                  loading={membershipState.loading}
                >
                  {attachedMemberships.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center py-4">
                        {t("No memberships attached to this product")}
                      </td>
                    </tr>
                  ) : (
                    attachedMemberships.map(membership => (
                      <tr key={membership.id}>
                        <td>
                          <div>
                            <p className="lead-text">{membership.title}</p>
                          </div>
                        </td>
                        <td>{membership.subtitle || "-"}</td>
                        <td>
                          <Badge type="primary">
                            {membership.membership_type === "membership"
                              ? t("Membership")
                              : t("Add-on")}
                          </Badge>
                        </td>
                        <td>
                          {membership.variant_id && product?.variants?.length > 1
                            ? product.variants.find(v => v.id === membership.variant_id)?.title
                            : "-"}
                        </td>
                        <td>
                          <div className="flex gap-2 items-center justify-end">
                            <ButtonNew
                              type="text"
                              size="xsmall"
                              onClick={() => history.push(`/admin/membership/${membership.id}`)}
                            >
                              {t("View")}
                              <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="ml-2" />
                            </ButtonNew>
                            <ButtonNew
                              type="text"
                              size="xsmall"
                              onClick={() => updateAttachMembershipModal({ isOpen: true })}
                              disabled={readOnly}
                            >
                              <FontAwesomeIcon icon={faArrowLeftArrowRight} />
                            </ButtonNew>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </DataTable>
              )}
            </Card.Section>
          </Card>) : null}

          {product.type === "Group Membership" ? (
            <>
              <IncludedMembershipsCard
                included={includedMemberships}
                onChange={setIncludedMemberships}
                disabled={readOnly}
              />
              <AddonMappingsCard
                mappings={addonMappings}
                modifierVariants={addonModifierVariants}
                onChange={setAddonMappings}
                disabled={readOnly}
              />
            </>
          ) : null}

        </Page.Section>
      )}
      {/* End of Product Logistics */}

      {/**
       * VARIANTS
       */}
      {selected === 3 ? (
        <>
          <Card
            title={t("secure.facility.product.product.022")}
            titleActions={[
              {
                content: (
                  <ButtonNew
                    onClick={() => history.push("/admin/product/" + productId + "/variant/new")}
                    disabled={
                      !authStore.user?.permissions?.products_create ||
                      !facilityStore.facility?.permissions?.products_create ||
                      product?.warehouse
                    }
                  >
                    {t("Add Variant")}
                  </ButtonNew>
                ),
                action: () => history.push("/admin/product/" + productId + "/variant/new"),
                disabled:
                  !authStore.user?.permissions?.products_create ||
                  !facilityStore.facility?.permissions?.products_create ||
                  product?.warehouse,
              },
            ]}
          >
            <Card.Section>
              <FormLayout>
                <FormLayout.Group>
                  {product?.options?.length > 0 ? (
                    <div className="ml-2 flex flex-wrap items-center gap-2">
                      <span className="text-sm text-gray-700">{t("Options: ")}</span>
                      {product.options.map((option, index) => (
                        <div className="flex items-center gap-2" key={option.name ?? index}>
                          <Badge size="small" type="gray">
                            {option.name}
                          </Badge>
                          {index !== product.options.length - 1 && <span className="text-sm text-semibold text-gray-400">{"/"}</span>}
                        </div>
                      ))}
                    </div>
                  ) : null}
                </FormLayout.Group>
                <FormLayout.Group>
                  <DataTable
                    columns={[
                      { label: "Name", width: "50%" },
                      { label: "Price", width: "45%" },
                      { label: "Actions", width: "5%" },
                    ]}
                  >
                    {product?.variants?.map((variant, index) => (
                      <tr key={index}>
                        <td>
                          <Checkbox
                            size="medium"
                            checked={variant.facility_access ? variant.facility_access : false}
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                              variantFacilityAccessChange(variant.id, e.target.checked)
                            }
                            label={variant.title}
                          />
                        </td>
                        <td>
                          <LocaleCurrency amount={variant.price} />
                        </td>
                        <td>
                          <div className="flex items-center gap-2" style={{ justifyContent: "flex-end" }}>
                            <ButtonNew
                              type="text"
                              size="small"
                              onClick={() =>
                                history.push("/admin/product/" + productId + "/variant/" + String(variant.id))
                              }
                            >
                              {t("secure.facility.product.product.098")}
                            </ButtonNew>
                            {facilityStore.facility?.is_single_facility && product?.variants?.length > 1 ? (
                              <FontAwesomeIcon
                                icon={["far", "trash"]}
                                onClick={() => updateDeleteVariantModal({ isOpen: true, variantId: variant.id })}
                                style={{ cursor: "pointer" }}
                              />
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </DataTable>
                </FormLayout.Group>
                {product?.variants?.length > 1 ? (
                  <FormLayout.Group>
                    <Toggle
                      id="variant_multiselect"
                      checked={Boolean(product.variant_multiselect)}
                      onChange={e => handleChangeEventInput(e, product, setProduct)}
                      labelRight={t("Variant multi-select")}
                      labelSupport={
                        productModifiers.length > 0
                          ? t("Variant multi-select is unavailable when modifiers are attached to this product.")
                          : t(
                              "At point of sale, select multiple variants and quantities to add to order.",
                            )
                      }
                      disabled={
                        !authStore.user?.permissions?.products_edit ||
                        !facilityStore.facility?.permissions?.products_edit ||
                        productModifiers.length > 0
                      }
                    />
                  </FormLayout.Group>
                ) : null}
              </FormLayout>
            </Card.Section>
          </Card>

          {/* MODIFIERS */}
          <Card
            title={t("secure.facility.product.product.062")}
            titleActions={[
              {
                content: t("Add New Modifier"),
                action: () => updateModifierModal({ isOpen: true }),
              },
            ]}
          >
            <Card.Section>
              {productModifiers.map((group: TModifierGroup, index: number) => (
                <Fragment key={group.id}>
                  {index !== 0 ? <Divider /> : null}
                  <div
                    className="flex items-center mb-2"
                    style={{ justifyContent: "space-between", marginBottom: "-10px" }}
                  >
                    <p className="font-semibold">{group.title}</p>
                    <ButtonNew
                      type="secondary"
                      size="small"
                      onClick={() => void detachModifierGroupFromProduct(group.id)}
                    >
                      {t("secure.facility.product.product.064")}
                    </ButtonNew>
                  </div>
                </Fragment>
              ))}
              {productModifiers.length === 0 ? <div>No Modifiers selected</div> : null}
            </Card.Section>
          </Card>
        </>
      ) : null}

      {selected === 4 ? (
        <Page.Section>
          {/* SALES CHANNELS & Dates */}
          <Card title={t("secure.facility.product.product.057")}>
            <Card.Section>
              <FormLayout>
                <FormLayout.Group>
                  {salesChannels
                    .filter(channel => channel.type !== "point_of_sale")
                    .map(channel => (
                      <Toggle
                        key={channel.id}
                        id={String(channel.id)}
                        value={channel.facility_id}
                        labelTop={channel.title}
                        onChange={e => handleSalesChannelUpdate(e)}
                        checked={channel.checked}
                        disabled={!authStore.user?.permissions?.products_edit}
                      />
                    ))}
                </FormLayout.Group>
                {salesChannels.find(channel => channel.title.toLowerCase() === "online store")?.checked && (
                  <FormLayout.Group>
                    <CopyLink
                      label="Online Store"
                      link={
                        window.origin +
                        "/online-store/" +
                        facilityStore?.facility?.short_name +
                        "/product/" +
                        product.handle
                      }
                    />

                    {/* <button className="flex gap-3 justify-end w-full" onClick={moveToProductOnlineStore}>
                      <span className="edit-product-view-online-store-text">View in online store</span>
                      <FontAwesomeIcon
                        className="edit-product-view-online-store-icon"
                        icon={["far", "arrow-up-right-from-square"]}
                      />
                    </button> */}
                  </FormLayout.Group>
                )}
              </FormLayout>
            </Card.Section>
            <Card.Section>
              <FormLayout>
                <FormLayout.Group>
                  <Toggle
                    id="available_on_toggle"
                    labelRight={t("secure.facility.product.product.058")}
                    onChange={handleAvailableOnToggle}
                    checked={availableAtDate !== null}
                  //disabled={readOnly}
                  />
                </FormLayout.Group>
                <FormLayout.Group>
                  {availableAtDate === null ? null : (
                    <span className="flex flex-row items-center gap-4">
                      <div style={{ width: "100%" }}>
                        <DatePickerInput
                          months={1}
                          position={"left"}
                          startingDate={availableAtDate}
                          label={t("secure.facility.product.product.059")}
                          setStartingDate={(selectedDate: any) => {
                            if (authStore.user?.permissions?.products_edit) {
                              handleAvailableOnDateSelectorChange(selectedDate as Date);
                            }
                          }}
                          error={!availableOnDateIsValid()}
                        />
                      </div>

                      <div style={{ display: "flex", marginTop: "15px", width: "100%", alignItems: "center" }}>
                        <TimePick
                          value={product.available_on_time}
                          onChange={timeString => handleTimeAvailability("available_on_time", timeString)}
                          disabled={readOnly}
                          align="right"
                          status={!availableOnDateIsValid() ? "error" : undefined}
                          size="large"
                        />
                        <label style={{ marginLeft: "5px", marginBottom: "0px" }} htmlFor="">
                          {moment().tz(Intl.DateTimeFormat().resolvedOptions().timeZone).zoneAbbr()}
                        </label>
                      </div>
                    </span>
                  )}
                </FormLayout.Group>

                <FormLayout.Group>
                  <Toggle
                    id="unavailable_on_toggle"
                    labelRight={t("secure.facility.product.product.060")}
                    onChange={handleAvailableUntilToggle}
                    checked={availableUntilDate !== null}
                  //disabled={readOnly}
                  />
                </FormLayout.Group>
                <FormLayout.Group>
                  {availableUntilDate === null ? null : (
                    <span className="flex flex-row items-center gap-4">
                      <div style={{ width: "100%" }}>
                        <DatePickerInput
                          months={1}
                          position={"left"}
                          startingDate={availableUntilDate}
                          label={t("secure.facility.product.product.061")}
                          setStartingDate={(selectedDate: any) => {
                            if (authStore.user?.permissions?.products_edit) {
                              handleAvailableUntilDateSelectorChange(selectedDate as Date);
                            }
                          }}
                          error={!availableUntilDateIsValid()}
                        />
                      </div>
                      <div style={{ display: "flex", marginTop: "15px", width: "100%", alignItems: "center" }}>
                        <TimePick
                          value={product.unavailable_on_time}
                          onChange={timeString => handleTimeAvailability("unavailable_on_time", timeString)}
                          disabled={readOnly}
                          align="right"
                          status={!availableUntilDateIsValid() ? "error" : undefined}
                          size="large"
                        />
                        <label style={{ marginLeft: "5px", marginBottom: "0px" }} htmlFor="">
                          {moment().tz(Intl.DateTimeFormat().resolvedOptions().timeZone).zoneAbbr()}
                        </label>
                      </div>
                    </span>
                  )}
                </FormLayout.Group>
              </FormLayout>
            </Card.Section>
          </Card>
        </Page.Section>
      ) : null}

      {/* IMAGES */}
      {selected === 5 ? (
        <Page.Section>
          <Card>
            <Card.Section title={t("secure.facility.product.product.038")}>
              <div
                className={`product-image_grid${
                  product.variants?.length > 1 ? " product-image_grid--with-variants" : ""
                }`}
              >
                <div className="product-image-upload_container">
                  <div className="product-image-upload_content">
                    <input
                      className="product-image-upload_input"
                      type="file"
                      accept="image/*"
                      onChange={event => {
                        const file = event.target.files?.[0];
                        if (file) {
                          void uploadProductImage(file);
                        }
                        event.target.value = "";
                      }}
                      disabled={readOnly}
                    />
                    <p>
                      <FontAwesomeIcon icon={faUpload} />
                    </p>
                  </div>
                </div>
                {productImages.map(image => {
                  const attachedVariantId =
                    product.variants?.length > 1
                      ? product.variants.find(variant => variant.image_id === image.id)?.id
                      : undefined;

                  return (
                    <div key={image.id} className="product-image_item">
                      <div className="product-image_container">
                        <div className="product-image">
                          {!readOnly ? (
                            <div className="remove-button_container">
                              <div
                                className="remove-button"
                                onClick={() => void deleteProductImage(image.id)}
                              >
                                <FontAwesomeIcon icon={faXmark} />
                              </div>
                            </div>
                          ) : null}
                          <img src={image.source} alt={t("Product image")} />
                        </div>
                      </div>
                      {product.variants?.length > 1 ? (
                        <div className="product-image_variants">
                          <SelectNew
                            id="variant_image_select"
                            label={t("Variant")}
                            placeholder={t("None")}
                            value={attachedVariantId}
                            options={product.variants.map(variant => ({
                              id: variant.id,
                              label: variant.title || variant.option1 || String(variant.id),
                            }))}
                            onChange={variantId => void assignProductImageVariant(image.id, Number(variantId))}
                            onClear={() => void assignProductImageVariant(image.id, null)}
                            disabled={readOnly}
                          />
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </Card.Section>
          </Card>

          {/* COLOR */}
          <Card>
            <Card.Section title={t("secure.facility.product.product.039")}>
              <PresetAndCustomColorPicker
                colorValue={product?.meta?.button_color ?? ""}
                onChange={(updatedHexColor: string | null) => {
                  setProduct(prev => ({
                    ...prev,
                    meta: { ...prev.meta, button_color: updatedHexColor },
                  }));
                }}
              />
            </Card.Section>
          </Card>
        </Page.Section>
      ) : null}

      {/* <Card title="Facilities Inventory" collapsable defaultCollapsed>
            <Card.Section>
              <div className="product-edit-inventory-table">
                <FacilityInventoryTable
                  mainFacilityId={facilityStore.facility?.id || 0}
                  facilities={facilityStore.facilities}
                  variants={product.variants}
                  showAllFacilities={showAllFaciliityInventories}
                />
              </div>
              <ButtonNew
                type="secondary"
                onClick={() => setShowAllFaciliityInventories(!showAllFaciliityInventories)}
                className="mt-4"
              >
                {showAllFaciliityInventories ? "Hide Secondary Locations" : "View All Locations"}{" "}
              </ButtonNew>
            </Card.Section>
          </Card> */}

      <Portal isMounted={modifierModal.isOpen}>
        <Sheet
          open={modifierModal.isOpen}
          title={t("secure.facility.product.product.080")}
          size="medium"
          closable
          onCancel={closeModifierModal}
          cancelText={t("secure.facility.product.product.081")}
          onOk={() => handleModifierModalOnOk()}
          okText={t("secure.facility.product.product.082")}
          okDisabled={modifierModal.groupIds.length === 0 ? true : false}
        >
          <div>
            <div style={{ top: "55px", width: "94%" }} className="absolute bg-white z-20 h-6"></div>
            <div className="sticky-top bg-white z-20 border-b-2 border-gray-200">
              <div className="relative flex items-center">
                <div className="flex-1">
                  {/* Do not need bounceback */}
                  <Input
                    onChange={e => updateModifierModal({ search: e.target.value })}
                    value={modifierModal.search}
                    type="search"
                    placeholder={t("secure.facility.product.product.083")}
                  />
                </div>
                <div className="mt-4 ml-4">
                  <Checkbox
                    size="medium"
                    label={t("secure.facility.product.product.084")}
                    checked={modifierModal.required}
                    onChange={() => updateModifierModal({ required: !modifierModal.required })}
                  />
                </div>
              </div>
            </div>
            <br />
            <DataTable
              columns={[
                { label: "", width: "5%" },
                { label: t("secure.facility.product.product.132"), width: "95%" },
              ]}
            >
              {modifierGroups
                .filter(
                  filteredGroup =>
                    !productModifiers.some(group => group.id === filteredGroup.id) &&
                    filteredGroup.title.toLowerCase().includes(modifierModal.search.toLowerCase()),
                )
                .map(modifierGroup => (
                  <tr
                    onClick={() =>
                      updateModifierModal({
                        groupIds: modifierModal.groupIds.includes(modifierGroup.id) ? [] : [modifierGroup.id],
                      })
                    }
                    className="clickable"
                    key={modifierGroup.id}
                  >
                    <td>
                      <Checkbox
                        size="small"
                        checked={modifierModal.groupIds.includes(modifierGroup.id)}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => e.stopPropagation()}
                      />
                    </td>

                    <td>{modifierGroup.title}</td>
                  </tr>
                ))}
            </DataTable>
          </div>
        </Sheet>
      </Portal>

      <Portal isMounted={newComboProductModal.isOpen}>
        <AttachProductAsComboModal
          isVisible={newComboProductModal.isOpen}
          currentlyAddedProducts={comboProducts}
          userLevel={"facility"}
          onCancel={() => closeNewComboProductModal()}
          onOk={productId => handleComboProductModalOk(productId)}
        />
      </Portal>

      <Portal isMounted={editComboProductModal.isOpen}>
        <UpdateProductAsComboModal
          isDynamic={product.dynamic_combo}
          isVisible={editComboProductModal.isOpen}
          onCancel={() => closeEditComboProductModal()}
          onOk={(comboId, cost, price, quantity) => handleUpdateComboProductModalOk(comboId, cost, price, quantity)}
          combo={editComboProductModal.selectedComboVariant}
          handleUpdates={updateEditComboProductModal}
        />
      </Portal>

      {/* DETACH product from facility.  Navigate to Products.tsx after. */}
      <Portal isMounted={removeProductModal.isOpen}>
        <Popup
          okText={t("secure.facility.product.product.073")}
          onCancel={() => closeRemoveProductModal()}
          onOk={() => detachProductFromfacility()}
          title={t("secure.facility.product.product.074")}
          type="warning"
          description={t("secure.facility.product.product.075")}
          open={removeProductModal.isOpen}
        />
      </Portal>

      {/* DUPLICATE product.  Navigate to Products.tsx after. */}
      <Portal isMounted={duplicateProductModal.isOpen}>
        <Popup
          okText={t("Confirm")}
          onCancel={() => closeDuplicateProductModal()}
          onOk={async () => {
            const ok = await duplicateProduct();
            if (ok) {
              closeDuplicateProductModal();
            }
          }}
          title={t("secure.facility.product.product.133")}
          type="warning"
          description={t("secure.facility.product.product.134")}
          open={duplicateProductModal.isOpen}
        />
      </Portal>

      <Portal isMounted={archiveModal.isOpen}>
        <Popup
          okText={t("Confirm")}
          onCancel={() => closeArchiveModal()}
          onOk={() => archiveProduct()}
          title={t("Archive Product")}
          type="warning"
          description={t("Are you sure you want to archive this product?")}
          open={archiveModal.isOpen}
        />
      </Portal>

      {/* Set kitchen locations, does not generate API call. onOk syncs with product.meta state. */}
      <Portal isMounted={kitchenLocationModal.isOpen}>
        <Sheet
          open={kitchenLocationModal.isOpen}
          size="small"
          onCancel={() => closeKitchenModal()}
          onOk={() => syncKitchenLocations()}
          title={t("secure.facility.product.product.068")}
          supportText={t("secure.facility.product.product.069")}
          overflow
        >
          <FormLayout>
            <FormLayout.Group>
              <Select
                label=""
                onChange={(id: any) => updateKitchenModal({ kitchen_location_id: id })}
                defaultValue={kitchenLocationModal.kitchen_location_id}
                disabled={!authStore.user?.permissions?.products_edit}
              >
                <Select.Option key={"none"} value={-1} name="none">
                  {t("secure.facility.product.product.135")}
                </Select.Option>
                {kitchenLocations
                  .filter((location: any) => {
                    // Filter out other selected kitchen_location_ids
                    if (
                      location.id !== kitchenLocationModal.kitchen_location_id_2 &&
                      location.id !== kitchenLocationModal.kitchen_location_id_3
                    ) {
                      return location;
                    }
                  })
                  .map((location, index: number) => {
                    return (
                      <Select.Option key={index} value={location.id} name={location.title}>
                        {location.title}
                      </Select.Option>
                    );
                  })}
              </Select>
            </FormLayout.Group>
            <FormLayout.Group>
              <Select
                label=""
                onChange={(id: any) => updateKitchenModal({ kitchen_location_id_2: id })}
                defaultValue={kitchenLocationModal.kitchen_location_id_2}
                disabled={!authStore.user?.permissions?.products_edit}
              >
                <Select.Option key={"none"} value={-1} name="none">
                  {t("secure.facility.product.product.135")}
                </Select.Option>
                {kitchenLocations
                  .filter((location: any) => {
                    // Filter out other selected kitchen_location_ids
                    if (
                      location.id !== kitchenLocationModal.kitchen_location_id &&
                      location.id !== kitchenLocationModal.kitchen_location_id_3
                    ) {
                      return location;
                    }
                  })
                  .map((location, index: number) => {
                    return (
                      <Select.Option key={index} value={location.id} name={location.title}>
                        {location.title}
                      </Select.Option>
                    );
                  })}
              </Select>
            </FormLayout.Group>
            <FormLayout.Group>
              <Select
                label=""
                onChange={(id: any) => updateKitchenModal({ kitchen_location_id_3: id })}
                defaultValue={kitchenLocationModal.kitchen_location_id_3}
                disabled={!authStore.user?.permissions?.products_edit}
              >
                <Select.Option key={"none"} value={-1} name="none">
                  {t("secure.facility.product.product.149")}
                </Select.Option>
                {kitchenLocations
                  .filter((location: any) => {
                    // Filter out other selected kitchen_location_ids
                    if (
                      location.id !== kitchenLocationModal.kitchen_location_id &&
                      location.id !== kitchenLocationModal.kitchen_location_id_2
                    ) {
                      return location;
                    }
                  })
                  .map((location, index: number) => {
                    return (
                      <Select.Option key={index} value={location.id} name={location.title}>
                        {location.title}
                      </Select.Option>
                    );
                  })}
              </Select>
            </FormLayout.Group>
          </FormLayout>
        </Sheet>
      </Portal>

      <Portal isMounted={printLabelModal.isOpen}>
        <Sheet
          open={printLabelModal.isOpen}
          size="small"
          onCancel={() => closePrintLabelModal()}
          onOk={() => printLabelModalOk()}
          okDisabled={isPrintLabelsDisabled()}
          title={t("secure.facility.product.product.065")}
        >
          <FormLayout>
            <FormLayout.Group>
              <Select
                onChange={(value: string) => updatePrintLabelModal({ template_id: value })}
                label={t("Template")}
                allowClear
                defaultValue={printLabelModal.template_id}
              >
                <Select.Option value={"5160"}>{"Avery 5160"}</Select.Option>
                <Select.Option value={"zebra_2x1"}>{"Zebra 2x1"}</Select.Option>
              </Select>
            </FormLayout.Group>
            <FormLayout.Group>
              <Input
                label={t("secure.facility.product.product.066")}
                value={printLabelModal.offset}
                onChange={(event: any) => updatePrintLabelModal({ offset: event.target.value })}
              />
            </FormLayout.Group>

            {labelVariants
              .sort((previousVariant, nextVariant) => previousVariant.position - nextVariant.position)
              .map(variant => (
                <FormLayout.Group key={variant.id}>
                  <Input
                    label={variant.title}
                    value={variant.quantity}
                    onChange={event =>
                      setLabelVariants(
                        labelVariants.map(vari =>
                          vari.id !== variant.id ? vari : { ...vari, quantity: Number(event.target.value) },
                        ),
                      )
                    }
                  />
                </FormLayout.Group>
              ))}
          </FormLayout>
        </Sheet>
      </Portal>

      <Portal isMounted={addTicketModal.isOpen}>
        <Sheet
          open={addTicketModal.isOpen}
          title={t("secure.facility.product.product.085")}
          size="small"
          closable
          onCancel={() => updateAddTicketModal({ isOpen: false, search: "" })}
          cancelText={t("secure.facility.product.product.086")}
          backDropCancel={false}
        >
          <div className="ui-checkbox-group" style={{ minHeight: "250px" }}>
            <div className="mb-4 w-full">
              <Search
                historyKey={"product_edit-ticket_search"}
                searchCallback={searchValue => updateAddTicketModal({ search: searchValue })}
                placeholder={t("secure.facility.product.product.137")}
              />
            </div>
            {tickets === undefined ? (
              <div className="ui-checkbox-group-item" style={{ height: "72px", justifyContent: "center" }}>
                <Spin />
              </div>
            ) : (
              tickets
                .filter(val => product.variants?.[0]?.tickets?.every(ticket => ticket.id !== val.id) ?? true)
                .map(ticket => {
                  return (
                    <div
                      key={ticket.id}
                      onClick={() => handleAddTicket(ticket.id)}
                      className="ui-checkbox-group-item clickable"
                    >
                      <div className="ui-checkbox-group-item-content">
                        <div className="ui-checkbox-group-item-text">
                          <p className="text-sm text-medium text-gray-700">{ticket.title}</p>
                          <p className="text-xs text-medium text-gray-500">{ticket.subtitle}</p>
                          <p className="text-xs text-regular text-gray-500">{ticket.included}</p>
                        </div>
                      </div>
                      <p className="text-m text-large">{t("secure.facility.product.product.138")}</p>
                    </div>
                  );
                })
            )}
          </div>
        </Sheet>
      </Portal>

      <Portal isMounted={inventoryModal.isOpen}>
        <Sheet
          open={inventoryModal.isOpen}
          size="small"
          closable
          title={t("Inventory Log")}
          cancelText={t("Cancel")}
          onCancel={() => {
            updateInventoryModal({ isOpen: false });
            setInventoryLogs([]);
          }}
        >
          <div className="flex flex-column">
            {inventoryLogs.map((logs, index) => (
              <InventoryLogs
                title={logs.type}
                action={logs.type}
                description={logs.description}
                key={index}
                previous_quantity={logs.previous_quantity}
                new_quantity={logs.new_quantity}
                last={index + 1 === inventoryLogs.length ? true : false}
                created_at={logs.created_at}
              />
            ))}
          </div>
        </Sheet>
      </Portal>

      <Portal isMounted={taxModal.isOpen}>
        <Sheet
          open={taxModal.isOpen}
          title={t("secure.facility.product.product.087")}
          size="medium"
          closable
          onCancel={closeTaxModal}
          cancelText={t("secure.facility.product.product.088")}
          backDropCancel={false}
        >
          {product.variants.map((variant: any, index: any) => {
            return (
              <div key={index} className="edit-tax-lines_container">
                <div className="edit-tax-lines_title_container">
                  <p className="edit-tax-lines_title">{variant.title}</p>
                </div>

                <div className="edit-tax-lines_single_variant_container">
                  <TagInputSelect
                    tags={variant.inventory_item?.inventory_level?.tax_lines.map((line: TTaxLines) => {
                      return { name: `${line.name} (${line.percentage}%)`, id: line.id };
                    })}
                    selectOptions={taxlines
                      ?.filter(
                        line =>
                          variant.inventory_item.inventory_level.tax_lines?.length === 0 ||
                          variant.inventory_item.inventory_level.tax_lines.findIndex(
                            (taxLine: { id: string | number }) => Number(taxLine.id) === Number(line.id),
                          ) < 0,
                      )
                      ?.map(line => {
                        return {
                          name: `${line.name} (${line.percentage}%) - ${line.province_code ?? ""}`,
                          id: line.id,
                        };
                      })}
                    onChange={value => {
                      handleTaxDropdown(Number(value), variant as IVariant);
                    }}
                    onTagClick={taxLineId => handleRemoveTaxLine(variant as IVariant, taxLineId)}
                    className="edit-tax-lines_single_variant_container_tag_input"
                    helpText={t("secure.facility.product.product.139")}
                  />
                </div>
              </div>
            );
          })}
        </Sheet>
      </Portal>

      <Portal isMounted={formsModal.isOpen}>
        <Sheet
          open={formsModal.isOpen}
          size="medium"
          closable
          onOk={() => updateFormsModal({ formToVariantVisible: true })}
          title={t("Forms")}
          okText={t("secure.facility.product.product.140")}
          onCancel={closeFormsModal}
          backDropCancel
        >
          <DataTable
            columns={[
              { label: t("secure.facility.product.product.141") },
              { label: t("secure.facility.product.product.142") },
            ]}
            loading={forms.variants === undefined}
          >
            {forms.variants?.map(state => (
              <tr key={state.variant?.id}>
                <td>{state.variant?.title}</td>
                <td>
                  <div className="flex gap-2">
                    {state.forms?.map(variantForm => (
                      <Badge key={variantForm?.id} size="small" type="primary">
                        {variantForm?.form?.title} -
                        <span
                          className="cursor-pointer ml-1 flex"
                          onClick={() =>
                            updateFormsModal({
                              removeFromVariantId: variantForm.variant_id,
                              removeFormId: variantForm.form.id,
                              removeFormVisible: true,
                            })
                          }
                        >
                          <FontAwesomeIcon icon={faXmark} />
                        </span>
                      </Badge>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </DataTable>
        </Sheet>
      </Portal>

      <Portal isMounted={formsModal.formToVariantVisible}>
        <Sheet
          open={formsModal.formToVariantVisible}
          title={t("secure.facility.product.product.143")}
          size="small"
          closable
          onCancel={() => updateFormsModal({ formToVariantVisible: false, variant_id: -1, form_id: -1 })}
          onOk={() => handleFormsModalOk()}
          okText={t("secure.facility.product.product.144")}
          okDisabled={formsModal.form_id === -1 || formsModal.variant_id === -1}
          stacked
          overflow
        >
          <div className="flex justify-between">
            <div className="w-full mr-2">
              <Select
                label={t("secure.facility.product.product.145")}
                onChange={(value: number) => updateFormsModal({ variant_id: value })}
                defaultValue={formsModal.variant_id}
                placeholder={t("secure.facility.product.product.146")}
              >
                <Select.Option value={-1}>{"None"}</Select.Option>
                {product?.variants.map(variant => {
                  return (
                    <Select.Option key={variant.id} value={variant.id} name={variant.title}>
                      {variant.title}
                    </Select.Option>
                  );
                })}
              </Select>
            </div>

            <div className="w-full ml-2">
              <Select
                label={t("secure.facility.product.product.147")}
                defaultValue={formsModal.form_id}
                onChange={(value: number) => updateFormsModal({ form_id: value })}
                placeholder={t("secure.facility.product.product.148")}
              >
                <Select.Option value={-1}>{t("secure.facility.product.product.149")}</Select.Option>
                {forms.all.sort()?.map(form => {
                  return (
                    <Select.Option key={form.id} value={form.id} name={form.title}>
                      {form.title}
                    </Select.Option>
                  );
                })}
              </Select>
            </div>
          </div>
        </Sheet>
      </Portal>

      <Portal isMounted={formsModal.removeFormVisible}>
        <Popup
          okText={t("secure.facility.product.edit.product_edit.022")}
          onCancel={() => updateFormsModal({ removeFormVisible: false, removeFromVariantId: null, removeFormId: null })}
          onOk={handleRemoveFormsModalOk}
          title={t("secure.facility.product.edit.product_edit.023")}
          type="warning"
          description={t("secure.facility.product.edit.product_edit.024")}
          open={formsModal.removeFormVisible}
        />
      </Portal>

      <Portal isMounted={discountModal.isOpen}>
        <Sheet
          open={discountModal.isOpen}
          title={t("New Discount")}
          size="medium"
          closable
          onCancel={() => closeDiscountModal()}
          onOk={() => {
            void addDiscount(discountModal);
            closeDiscountModal();
          }}
          cancelText={t("secure.facility.product.product.077")}
          okText={t("Confirm")}
          backDropCancel={false}
        >
          <Input
            label={t("secure.facility.product.product.012")}
            value={discountModal.title}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateDiscountModal({ title: e.target.value })}
            type="text"
            id={"description"}
            placeholder={t("Discount name or description")}
          />
          <Card.Section>
            <span className="flex flex-row gap-4 mt-3">
              <SelectNew
                id="defaultDiscount"
                label={t("Default Discount")}
                placeholder={t("secure.facility.product.product.127")}
                value={discountModal.value_type}
                options={[
                  {
                    label: t("Above Cost"),
                    id: "0",
                  },
                  {
                    label: t("Below Retail"),
                    id: "1",
                  },
                ]}
                onChange={value => updateDiscountModal({ value_type: value })}
                className="w-1/2"
              />
              <div className="w-1/2 mt-1.5">
                <Input
                  label={t("Value (%)")}
                  value={discountModal.value}
                  onChange={e => updateDiscountModal({ value: Number(e.target.value) })}
                  id={"value"}
                  placeholder="0"
                />
              </div>
            </span>
          </Card.Section>
          <br />
          <Card.Section className="mt-4">
            <Checkbox
              id="scheduledDiscount"
              size="medium"
              checked={discountModal.scheduledDiscount}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                updateDiscountModal({ scheduledDiscount: e.target.checked })
              }
              label={t("Scheduled Discount")}
            />
          </Card.Section>
          <br />
          {discountModal.scheduledDiscount ? (
            <Card.Section>
              <div className="flex flex-row gap-4">
                <div className="w-1/2">
                  <DatePickerInput
                    label={t("Schedule Discount")}
                    months={2}
                    position="left"
                    startingDate={discountModal.start_date}
                    setStartingDate={setDateRangeStart}
                    endingDate={discountModal.end_date}
                    setEndingDate={setDateRangeEnd}
                    //multipleDates={discountModal.datesRange}
                    disablePastDays
                  />
                </div>
                <div className="w-1/4" style={{ marginTop: "0.5rem" }}>
                  {/* <Input
                      label="Start Time"
                      value={discountModal.startTime}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        updateDiscountModal({ startTime: e.target.value })
                      }
                      id={"startTime"}
                      type="time"
                    /> */}
                  <TimePick
                    label="Start Time"
                    value={discountModal.startTime}
                    onChange={value => updateDiscountModal({ startTime: value })}
                  />
                </div>
                <div className="w-1/4" style={{ marginTop: "0.5rem" }}>
                  {/* <Input
                      label="End Time"
                      value={discountModal.endTime}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        updateDiscountModal({ endTime: e.target.value })
                      }
                      id={"endTime"}
                      type="time"
                    /> */}
                  <TimePick
                    label="End Time"
                    value={discountModal.endTime}
                    onChange={value => updateDiscountModal({ startTime: value })}
                  />
                </div>
              </div>
              <br />
            </Card.Section>
          ) : null}
          <SelectNew
            label={t("Sales Channel")}
            value={discountModal.salesChannel}
            onChange={value => updateDiscountModal({ salesChannel: value })}
            options={salesChannels.map((channel: any) => {
              return { label: channel.title, id: channel.id };
            })}
            id={"salesChannel"}
            placeholder={t("Find sales channel")}
          />
          <br />
          <SelectNew
            label={t("Customer Type")}
            multiValue={selectedCustomerType}
            onChange={value => toggleCustomerType(value)}
            options={customerTypes.map((type: any) => {
              return { label: type.title, id: type.id };
            })}
            id={"customerType"}
            placeholder={t("Find customer type")}
          />
          <br />
          <DataTable
            columns={[
              {
                label: " ",
                content: (
                  <Checkbox
                    id="selectAllDiscounts"
                    size="medium"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleAllCustomerTypeCheckbox(e)}
                  />
                ),
                width: "5%",
              },
              { label: t("Customer Type"), width: "90%" },
              {
                label: t("Remove all"),
                content: (
                  <div
                    onClick={() => setSelectedCustomerType([])}
                    style={{ fontSize: "12px", fontWeight: 500, color: "#475467", cursor: "pointer" }}
                  >
                    {t("Remove all")}
                  </div>
                ),
                width: "5%",
              },
            ]}
          >
            {selectedCustomerType.map((type: any, index: number) => {
              return (
                <tr key={type.id}>
                  <td>
                    <Checkbox
                      id={type}
                      size="medium"
                      value={discountModal.customerType || false}
                      //checked={Array.isArray(discountModal.customerType) ? discountModal.customerType.includes(type) : false}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleCustomerTypeCheckbox(e, index)}
                    />
                  </td>
                  <td>{customerTypes.find(customerType => customerType.id === type).title}</td>
                  <td>
                    <div
                      id={type}
                      className="flex gap-2 hover:cursor-pointer justify-center"
                      onClick={() => {
                        toggleCustomerType(type as number);
                      }}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </div>
                  </td>
                </tr>
              );
            })}
          </DataTable>
        </Sheet>
      </Portal>

      <Portal isMounted={deleteVariantModal.isOpen}>
        <Popup
          onCancel={closeDeleteVariantModal}
          onOk={handleArchiveVariant}
          title={t("Delete Variant")}
          type="delete"
          description={t("Are you sure you want to delete this variant? This action cannot be undone.")}
          open={deleteVariantModal.isOpen}
        >
          <div></div>
        </Popup>
      </Portal>


      <Portal isMounted={attachMembershipModal.isOpen}>
        <Sheet
          open={attachMembershipModal.isOpen}
          title={t("Attach Membership")}
          size="medium"
          closable
          onCancel={() => {
            closeAttachMembershipModal();
            setSelectedMembershipId(null);
            setMembershipSearchQuery("");
          }}
          onOk={handleAttachMembership}
          okText={t("Attach")}
          cancelText={t("Cancel")}
          okDisabled={!selectedMembershipId}
          backDropCancel={false}
        >
          <div className="mb-4">
            <Search
              searchCallback={value => setMembershipSearchQuery(value)}
              historyKey="attach-membership-search"
              placeholder={t("Search memberships...")}
            />
          </div>

          <div style={{ minHeight: "300px" }}>
            {availableMemberships === undefined ? (
              <div className="flex justify-center items-center" style={{ height: "300px" }}>
                <Spin />
              </div>
            ) : (
              <DataTable
                columns={[
                  { label: "", width: "5%" },
                  { label: t("Membership"), width: "50%" },
                  { label: t("Type"), width: "20%" },
                  { label: t("Status"), width: "15%" },
                  { label: t("Customer Type"), width: "10%" },
                ]}
              >
                {availableMemberships?.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-4">
                      {t("No memberships available to attach")}
                    </td>
                  </tr>
                ) : (
                  availableMemberships?.map(membership => (
                    <tr
                      key={membership.id}
                      onClick={() => setSelectedMembershipId(membership.id)}
                      className="clickable"
                      style={{
                        backgroundColor:
                          selectedMembershipId === membership.id ? "#F9FAFB" : "transparent",
                      }}
                    >
                      <td>
                        <input
                          type="radio"
                          checked={selectedMembershipId === membership.id}
                          onChange={() => setSelectedMembershipId(membership.id)}
                          onClick={e => e.stopPropagation()}
                        />
                      </td>
                      <td>
                        <div>
                          <p className="lead-text">{membership.title}</p>
                          {membership.subtitle && (
                            <p className="support-text">{membership.subtitle}</p>
                          )}
                        </div>
                      </td>
                      <td>
                        <Badge type="primary">
                          {membership.membership_type === "membership"
                            ? t("Membership")
                            : t("Add-on")}
                        </Badge>
                      </td>
                      <td>
                        <Badge type={membership.archived ? "error" : "success"}>
                          {membership.archived ? t("Archived") : t("Active")}
                        </Badge>
                      </td>
                      <td>{membership.customer_type_title || "-"}</td>
                    </tr>
                  ))
                )}
              </DataTable>
            )}
          </div>
        </Sheet>
      </Portal>
    </Page>
  );
}
